import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import "@/index.css";
import { Toaster, toast } from "sonner";
import { ComfortCornerPage } from "./components/pages/ComfortCornerPage";
import { SharePage } from "./components/pages/SharePage";
import { MomentsPage } from "./components/pages/MomentsPage";
import { JournalPage } from "./components/pages/JournalPage";
import { HappyEndingsPage } from "./components/pages/HappyEndingsPage";
import { StorybookCreatorPage } from "./components/pages/StorybookCreatorPage";
import { HowToUsePage } from "./components/pages/HowToUsePage";
import { SongManagerPage } from "./components/pages/SongManagerPage";
import { CollectionsPage } from "./components/pages/CollectionsPage";
import { 
  Home as HomeIcon, 
  Heart, 
  Music, 
  Camera, 
  FolderOpen, 
  Plus, 
  X, 
  Edit2, 
  Trash2,
  Sparkles,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Calendar,
  BookHeart,
  Share2,
  Star,
  Award,
  Book,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  Utensils,
  Clock,
  Stethoscope,
  Gamepad2,
  Flower2,
  Wind,
  LayoutGrid,
  BookOpen,
  Printer,
  Download,
  FileText,
  User,
  Mail,
  Lock,
  LogOut,
  Quote,
  Candle,
  Images,
  HeartHandshake,
  Lamp,
  PawPrint,
  Settings,
  Eye,
  EyeOff,
  Key,
  Search,
  Repeat,
  RefreshCw,
  Feather,
  HelpCircle
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== AUTH HELPER ====================
const getAuthHeader = () => {
  const token = localStorage.getItem('pawsToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const axiosAuth = axios.create();
axiosAuth.interceptors.request.use(config => {
  const token = localStorage.getItem('pawsToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== WELCOME BACKGROUNDS ====================
const WELCOME_BACKGROUNDS = [
  "https://images.unsplash.com/photo-1769117320704-b4d7d21ada85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBldHMlMjBkb2dzJTIwY2F0cyUyMGNvbG9yZnVsJTIwam95ZnVsfGVufDB8fHx8MTc3MjM5ODM4M3ww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1509205477838-a534e43a849f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHBldHMlMjBkb2dzJTIwY2F0cyUyMGNvbG9yZnVsJTIwam95ZnVsfGVufDB8fHx8MTc3MjM5ODM4M3ww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1618118968094-2fcd4df5d101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx3YXRlcmNvbG9yJTIwZmxvd2VycyUyMHNwcmluZyUyMHBhc3RlbCUyMHNvZnR8ZW58MHx8fHwxNzcyMzk4Mzg0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1559836199-4ac20009a8a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHBldHMlMjBkb2dzJTIwY2F0cyUyMGNvbG9yZnVsJTIwam95ZnVsfGVufDB8fHx8MTc3MjM5ODM4M3ww&ixlib=rb-4.1.0&q=85"
];

// ==================== MOOD DATA ====================
const MOODS = [
  { emoji: "😊", name: "Happy", background: "https://images.unsplash.com/photo-1681763363228-dd5bc9c37d28?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-yellow-500/20 to-orange-400/30" },
  { emoji: "😌", name: "Peaceful", background: "https://images.unsplash.com/photo-1592976370975-dcc9e764374f?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-purple-500/20 to-pink-400/20" },
  { emoji: "🥰", name: "Loving", background: "https://images.unsplash.com/photo-1761166478784-dc565cffb472?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-pink-400/20 to-rose-300/30" },
  { emoji: "😴", name: "Cozy", background: "https://images.unsplash.com/photo-1767114210435-8122d97c95f8?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-amber-600/30 to-orange-500/30" },
  { emoji: "🤗", name: "Grateful", background: "https://images.unsplash.com/photo-1662359589930-bb721b78edba?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-amber-400/20 to-yellow-500/20" },
  { emoji: "🏖️", name: "Relaxed", background: "https://images.unsplash.com/photo-1683514261963-2c6db08a72fe?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-cyan-400/20 to-blue-400/20" },
  { emoji: "⚡", name: "Energetic", background: "https://images.unsplash.com/photo-1596769016576-166156a7afc9?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-orange-400/20 to-red-400/20" },
  { emoji: "🎉", name: "Playful", background: "https://images.unsplash.com/photo-1729608629987-510b57954c75?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-violet-400/30 to-fuchsia-400/30" },
  { emoji: "🌸", name: "Gentle", background: "https://images.unsplash.com/photo-1761332888266-a3ed50de3c58?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-green-300/20 to-emerald-400/20" },
  { emoji: "🌙", name: "Dreamy", background: "https://images.unsplash.com/photo-1755875479709-4698b71ec494?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-indigo-400/20 to-purple-400/20" },
  { emoji: "🐾", name: "Pawsitive", background: "https://images.unsplash.com/photo-1623945514418-15427b33a34c?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-lime-400/20 to-green-400/20" },
  { emoji: "🎵", name: "Musical", background: "https://images.unsplash.com/photo-1723018623131-03c1d20ac673?crop=entropy&cs=srgb&fm=jpg&w=800", overlay: "from-slate-400/20 to-stone-400/20" },
];

// ==================== DAILY INSPIRATION QUOTES ====================
const DAILY_QUOTES = [
  { quote: "The world would be a nicer place if everyone had the ability to love as unconditionally as a dog.", author: "M.K. Clinton", emoji: "🐕" },
  { quote: "Until one has loved an animal, a part of one's soul remains unawakened.", author: "Anatole France", emoji: "💕" },
  { quote: "Pets are humanizing. They remind us we have an obligation to preserve and nurture.", author: "James Cromwell", emoji: "🌱" },
  { quote: "The smallest feline is a masterpiece.", author: "Leonardo da Vinci", emoji: "🐱" },
  { quote: "Happiness is a warm puppy.", author: "Charles M. Schulz", emoji: "🐾" },
  { quote: "Animals are such agreeable friends—they ask no questions; they pass no criticisms.", author: "George Eliot", emoji: "🤗" },
  { quote: "Time spent with cats is never wasted.", author: "Sigmund Freud", emoji: "😺" },
  { quote: "Dogs do speak, but only to those who know how to listen.", author: "Orhan Pamuk", emoji: "👂" },
  { quote: "The purity of a person's heart can be quickly measured by how they regard animals.", author: "Anonymous", emoji: "❤️" },
  { quote: "What we have once enjoyed we can never lose. All that we love deeply becomes a part of us.", author: "Helen Keller", emoji: "🌟" },
  { quote: "No matter how little money and how few possessions you own, having a dog makes you rich.", author: "Louis Sabin", emoji: "💎" },
  { quote: "A house is not a home without a pet.", author: "Anonymous", emoji: "🏠" },
  { quote: "Pets leave paw prints on your heart.", author: "Anonymous", emoji: "🐾" },
  { quote: "In the eyes of an animal, you are their whole world.", author: "Anonymous", emoji: "🌍" },
  { quote: "The journey of life is sweeter when traveled with a pet.", author: "Anonymous", emoji: "🛤️" },
  { quote: "Every day is a gift—unwrap it with your furry friend.", author: "Anonymous", emoji: "🎁" },
  { quote: "Love is a four-legged word.", author: "Anonymous", emoji: "💝" },
  { quote: "Kindness to all living things is the true religion.", author: "Buddha", emoji: "☮️" },
  { quote: "The greatness of a nation can be judged by the way its animals are treated.", author: "Mahatma Gandhi", emoji: "🕊️" },
  { quote: "One small positive thought in the morning can change your whole day.", author: "Dalai Lama", emoji: "☀️" },
  { quote: "Be the person your pet thinks you are.", author: "Anonymous", emoji: "⭐" },
  { quote: "Every moment matters, especially the quiet ones with those we love.", author: "Anonymous", emoji: "🌸" },
  { quote: "Gratitude turns what we have into enough.", author: "Anonymous", emoji: "🙏" },
  { quote: "Let today be the day you spread joy like your pet spreads fur.", author: "Anonymous", emoji: "😄" },
  { quote: "In a world where you can be anything, be kind—like your pet.", author: "Anonymous", emoji: "💫" },
  { quote: "The best therapist has fur and four legs.", author: "Anonymous", emoji: "🛋️" },
  { quote: "Life is better with paw prints on the floor.", author: "Anonymous", emoji: "🐕‍🦺" },
  { quote: "Take time to pause and appreciate the small moments today.", author: "Anonymous", emoji: "⏸️" },
  { quote: "Your pet doesn't care about your past—they love who you are right now.", author: "Anonymous", emoji: "💖" },
  { quote: "May your day be filled with wagging tails and purring hearts.", author: "Anonymous", emoji: "🎶" }
];

const GRATITUDE_PROMPTS = [
  "What made you smile today with your pet?",
  "What's something your pet did that warmed your heart?",
  "What are you thankful for about your furry friend?",
  "What special moment did you share with your pet today?",
  "What do you love most about your pet right now?",
  "How did your pet bring you joy today?",
  "What's a small blessing your pet gave you today?",
  "What comfort did your pet bring you when you needed it?",
  "What funny thing did your pet do recently?",
  "How has your pet helped you feel less alone?",
  "What adventure did you share with your pet?",
  "What makes your bond with your pet special?",
  "How did cuddling with your pet make you feel?",
  "What lesson has your pet taught you about life?",
  "What's the sweetest thing your pet does for you?"
];

// ==================== WHY PETS MATTER ====================
const WHY_PETS_MATTER = [
  { lesson: "Being present", description: "They don't dwell on yesterday or worry about tomorrow" },
  { lesson: "Forgiving quickly", description: "A moment later, all is forgotten and love remains" },
  { lesson: "Noticing small joys", description: "A sunbeam, a treat, your return home — all miracles" },
  { lesson: "Loving without keeping score", description: "Their love is unconditional and infinite" }
];

// ==================== HOW TO SHOW LOVE ====================
const SHOW_LOVE_TIPS = [
  { tip: "\"Hello\" Ritual", description: "A calm greeting with a gentle touch when you see them", emoji: "👋" },
  { tip: "5 Minutes of Focus", description: "Put the phone down. Just play. Just be together.", emoji: "🎯" },
  { tip: "Create a Cozy Spot", description: "A safe blanket, bed, or corner that's just theirs", emoji: "🛏️" },
  { tip: "Use Your Soft Voice", description: "Pets understand tone — let yours say \"I love you\"", emoji: "💬" },
  { tip: "Slow Walks or Porch Time", description: "Let them sniff, explore, and take their time", emoji: "🚶" },
  { tip: "Little Training with Praise", description: "Builds confidence and deepens trust", emoji: "⭐" },
  { tip: "Calm-Down Routine", description: "Music + breathing + gentle petting = peace", emoji: "🎵" },
  { tip: "Health Check Habit", description: "Fresh water, clean bowls, a gentle body check", emoji: "💧" },
  { tip: "Gratitude Out Loud", description: "Say it: \"I'm so glad you're here.\"", emoji: "🙏" }
];

// ==================== BREATHING EXERCISES ====================
const BREATHING_EXERCISES = [
  {
    name: "4-7-8 Calming Breath",
    description: "A relaxing technique to calm your nervous system",
    steps: ["Breathe in through your nose for 4 seconds", "Hold your breath for 7 seconds", "Exhale slowly through your mouth for 8 seconds", "Repeat 3-4 times"],
    duration: "2-3 minutes",
    emoji: "🌬️"
  },
  {
    name: "Box Breathing",
    description: "Used by Navy SEALs to stay calm under pressure",
    steps: ["Inhale for 4 seconds", "Hold for 4 seconds", "Exhale for 4 seconds", "Hold for 4 seconds", "Repeat 4 times"],
    duration: "2 minutes",
    emoji: "📦"
  },
  {
    name: "Pet Cuddle Breathing",
    description: "Sync your breathing with your pet's calm presence",
    steps: ["Hold or sit near your pet", "Place your hand on their chest", "Match your breathing to their rhythm", "Feel your heartbeats sync together"],
    duration: "5 minutes",
    emoji: "🐾"
  },
  {
    name: "Morning Sunshine Breath",
    description: "Energize your morning with your pet",
    steps: ["Stand or sit with your pet nearby", "Inhale deeply, raising arms overhead", "Exhale, lowering arms and petting your companion", "Smile and repeat 5 times"],
    duration: "2 minutes",
    emoji: "☀️"
  },
  {
    name: "Evening Wind-Down",
    description: "Prepare for restful sleep with your pet",
    steps: ["Dim the lights and get cozy with your pet", "Take 10 slow, deep breaths", "With each exhale, release the day's stress", "Focus on the warmth of your pet beside you"],
    duration: "5 minutes",
    emoji: "🌙"
  }
];

const RELAXATION_TIPS = [
  { emoji: "🎵", tip: "Play calming music for you and your pet - classical or nature sounds work wonderfully" },
  { emoji: "🌿", tip: "Take a gentle walk in nature with your pet - fresh air heals the soul" },
  { emoji: "☕", tip: "Enjoy a warm cup of tea while your pet rests beside you" },
  { emoji: "📖", tip: "Read aloud to your pet - they love the sound of your voice" },
  { emoji: "🧘", tip: "Practice gentle stretches while your pet watches or joins in" },
  { emoji: "🌸", tip: "Create a cozy corner with soft blankets for you and your pet" },
  { emoji: "💆", tip: "Give your pet a gentle massage - it relaxes both of you" },
  { emoji: "🎨", tip: "Do a calming activity like coloring while your pet naps nearby" }
];

// ==================== AUDIO PLAYER ====================
const AudioPlayer = ({ audioFile, title }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(false);

  const togglePlay = () => { if (audioRef.current) { if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); } setIsPlaying(!isPlaying); } };
  const toggleMute = () => { if (audioRef.current) { audioRef.current.muted = !isMuted; setIsMuted(!isMuted); } };
  const toggleLoop = () => { if (audioRef.current) { audioRef.current.loop = !isLooping; setIsLooping(!isLooping); } };
  const handleTimeUpdate = () => { if (audioRef.current) { setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100 || 0); } };
  const handleSeek = (e) => { if (audioRef.current) { const rect = e.currentTarget.getBoundingClientRect(); audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * audioRef.current.duration; } };
  const handleEnded = () => { if (!isLooping) { setIsPlaying(false); setProgress(0); } };

  return (
    <div className="bg-sage/10 rounded-2xl p-3 mt-3">
      <audio ref={audioRef} src={audioFile} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded} />
      <div className="flex items-center gap-3">
        <button data-testid="audio-play-button" onClick={togglePlay} className="w-10 h-10 bg-sage text-white rounded-full flex items-center justify-center hover:bg-sage-dark transition-all">
          {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <div className="flex-1 h-2 bg-gray-200 rounded-full cursor-pointer" onClick={handleSeek}>
          <div className="h-full bg-sage rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
        <button onClick={toggleLoop} className={`transition-all ${isLooping ? 'text-terracotta' : 'text-sage hover:text-sage-dark'}`} title={isLooping ? "Loop On" : "Loop Off"}>
          <Repeat size={20} />
        </button>
        <button onClick={toggleMute} className="text-sage hover:text-sage-dark transition-all">{isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}</button>
      </div>
      {isLooping && <p className="text-xs text-terracotta mt-2 text-center">🔁 Looping enabled - perfect for extended play</p>}
    </div>
  );
};

// ==================== MOOD SELECTOR ====================
const MoodSelector = ({ currentMood, setCurrentMood, saveMoodEntry }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [moodNote, setMoodNote] = useState("");
  const [selectedMood, setSelectedMood] = useState(null);
  
  const handleMoodSelect = (mood) => { setSelectedMood(mood); setShowNoteInput(true); };
  const handleSaveMood = async () => { if (selectedMood) { setCurrentMood(selectedMood); localStorage.setItem('pawsHarmonyMood', JSON.stringify(selectedMood)); await saveMoodEntry(selectedMood, moodNote); toast.success(`Mood set to ${selectedMood.emoji} ${selectedMood.name}!`); setIsOpen(false); setShowNoteInput(false); setMoodNote(""); setSelectedMood(null); } };
  const handleSkipNote = async () => { if (selectedMood) { setCurrentMood(selectedMood); localStorage.setItem('pawsHarmonyMood', JSON.stringify(selectedMood)); await saveMoodEntry(selectedMood, ""); toast.success(`Mood set to ${selectedMood.emoji} ${selectedMood.name}!`); setIsOpen(false); setShowNoteInput(false); setMoodNote(""); setSelectedMood(null); } };

  return (
    <div className="relative">
      <button data-testid="mood-selector-button" onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50">
        <span className="text-2xl">{currentMood?.emoji || "😊"}</span>
        <span className="text-charcoal font-medium hidden sm:inline">{currentMood?.name || "Set Mood"}</span>
        <Sparkles size={16} className="text-terracotta" />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => {setIsOpen(false); setShowNoteInput(false); setSelectedMood(null);}} />
          <div className="absolute right-0 top-full mt-2 bg-white rounded-3xl shadow-2xl p-4 z-50 w-80">
            {!showNoteInput ? (
              <>
                <h3 className="font-heading text-lg font-semibold text-charcoal mb-3 text-center">How are you feeling? 💭</h3>
                <div className="grid grid-cols-4 gap-2">
                  {MOODS.map((mood) => (
                    <button key={mood.name} data-testid={`mood-${mood.name.toLowerCase()}`} onClick={() => handleMoodSelect(mood)} className={`flex flex-col items-center p-2 rounded-2xl transition-all duration-200 hover:scale-110 ${currentMood?.name === mood.name ? "bg-sage/20 ring-2 ring-sage" : "hover:bg-gray-100"}`}>
                      <span className="text-2xl mb-1">{mood.emoji}</span>
                      <span className="text-xs text-stone">{mood.name}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-4"><span className="text-4xl">{selectedMood?.emoji}</span><h3 className="font-heading text-lg font-semibold text-charcoal mt-2">Feeling {selectedMood?.name}!</h3></div>
                <textarea data-testid="mood-note-input" value={moodNote} onChange={(e) => setMoodNote(e.target.value)} placeholder="Add a note to your journal? 📝" className="w-full h-20 bg-gray-50 border-2 border-gray-200 focus:border-sage rounded-2xl px-4 py-3 text-sm outline-none resize-none mb-3" />
                <div className="flex gap-2">
                  <button onClick={handleSaveMood} className="flex-1 bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all">Save 📝</button>
                  <button onClick={handleSkipNote} className="px-4 py-3 text-stone hover:text-charcoal transition-all">Skip</button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ==================== NAVIGATION ====================
const Navigation = ({ currentPage, setCurrentPage, currentMood, setCurrentMood, saveMoodEntry, user, onLogout, onRefresh, onShowAuth, onShowWelcome, songs, pets }) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Show refresh button if data appears to be missing
  const dataMightBeMissing = songs?.length === 0 && pets?.length === 0;

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh?.();
    setRefreshing(false);
  };

  const navItems = user ? [
    { id: "home", label: "Home", icon: HomeIcon },
    { id: "pets", label: "Pets", icon: Heart },
    { id: "songbook", label: "Songbook", icon: Book },
    { id: "storybook", label: "Create", icon: Feather },
    { id: "stories", label: "Stories", icon: BookOpen },
    { id: "moments", label: "Moments", icon: Camera },
    { id: "journal", label: "Journal", icon: BookHeart },
    { id: "comfort", label: "Comfort", icon: Flower2 },
    { id: "share", label: "Share", icon: Share2 },
  ] : [
    // Public navigation - limited options
    { id: "home", label: "Home", icon: HomeIcon },
    { id: "songbook", label: "Songbook", icon: Book },
    { id: "stories", label: "Stories", icon: BookOpen },
  ];

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setChangingPassword(true);
    try {
      await axiosAuth.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully! 🔐");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to change password";
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <nav className="hidden md:flex fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 px-8 py-4">
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-sage">🐾 Paws & Harmony</h1>
          <div className="flex items-center gap-2">
            {navItems.map((item) => (
              <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => setCurrentPage(item.id)} className={`flex items-center gap-2 px-4 py-3 rounded-full text-sm font-medium transition-all duration-300 ${currentPage === item.id ? "bg-sage text-white" : "text-charcoal hover:bg-sage/10"}`}>
                <item.icon size={18} />{item.label}
              </button>
            ))}
            {user ? (
              <MoodSelector currentMood={currentMood} setCurrentMood={setCurrentMood} saveMoodEntry={saveMoodEntry} />
            ) : null}
            {/* User Menu */}
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
              {user ? (
                <>
                  <span className="text-sm text-stone">{user?.name}</span>
                  {user?.is_admin && <span className="text-xs bg-terracotta/10 text-terracotta px-2 py-1 rounded-full">Admin</span>}
                  {/* Help Menu */}
                  <div className="relative">
                    <button 
                      onClick={() => setShowHelpMenu(!showHelpMenu)} 
                      className="p-2 text-stone hover:text-sage transition-all" 
                      title="Help & Guide"
                    >
                      <HelpCircle size={18} />
                    </button>
                    {showHelpMenu && (
                      <div className="absolute right-0 top-10 bg-white rounded-xl shadow-lg border border-gray-200 py-2 w-48 z-50">
                        <button 
                          onClick={() => { setCurrentPage("guide"); setShowHelpMenu(false); }}
                          className="w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-sage/10 flex items-center gap-2"
                        >
                          <BookOpen size={16} /> How to Use Guide
                        </button>
                        {user?.is_admin && (
                          <button 
                            onClick={() => { setCurrentPage("song-manager"); setShowHelpMenu(false); }}
                            className="w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-sage/10 flex items-center gap-2"
                          >
                            <Music size={16} /> Song Manager
                          </button>
                        )}
                        {user?.is_admin && (
                          <button 
                            onClick={() => { setCurrentPage("collections"); setShowHelpMenu(false); }}
                            className="w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-sage/10 flex items-center gap-2"
                          >
                            <FolderOpen size={16} /> Collections
                          </button>
                        )}
                        <button 
                          onClick={() => { onShowWelcome?.(); setShowHelpMenu(false); }}
                          className="w-full px-4 py-2 text-left text-sm text-charcoal hover:bg-sage/10 flex items-center gap-2"
                        >
                          <Sparkles size={16} /> View Welcome
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Refresh button - always visible, highlighted if data might be missing */}
                  <button 
                    onClick={handleRefresh} 
                    disabled={refreshing}
                    className={`p-2 transition-all ${dataMightBeMissing ? 'text-amber-500 animate-pulse' : 'text-stone hover:text-sage'}`} 
                    title={dataMightBeMissing ? "Data not loaded - Click to refresh" : "Refresh data"}
                  >
                    <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                  </button>
                  <button onClick={() => setShowPasswordModal(true)} className="p-2 text-stone hover:text-sage transition-all" title="Change Password">
                    <Key size={18} />
                  </button>
                  <button onClick={onLogout} className="p-2 text-stone hover:text-red-500 transition-all" title="Logout">
                    <LogOut size={18} />
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => onShowAuth?.()} 
                  className="bg-sage text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-sage-dark transition-all"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-50 px-1 py-1 safe-area-inset-bottom">
        <div className="flex justify-around">
          {navItems.map((item) => (
            <button key={item.id} data-testid={`mobile-nav-${item.id}`} onClick={() => setCurrentPage(item.id)} className={`flex flex-col items-center gap-0.5 px-1.5 py-1.5 rounded-xl transition-all min-h-[44px] ${currentPage === item.id ? "text-sage bg-sage/10" : "text-stone"}`}>
              <item.icon size={18} /><span className="text-[9px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
      {/* Mobile header with user info */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 px-4 py-3 flex items-center justify-between">
        <h1 className="font-heading text-lg font-semibold text-sage">🐾 Paws</h1>
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <MoodSelector currentMood={currentMood} setCurrentMood={setCurrentMood} saveMoodEntry={saveMoodEntry} />
              {/* Refresh button for mobile */}
              <button 
                onClick={handleRefresh} 
                disabled={refreshing}
                className={`p-2 transition-all ${dataMightBeMissing ? 'text-amber-500 animate-pulse' : 'text-stone hover:text-sage'}`} 
                title="Refresh data"
              >
                <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button onClick={() => setShowPasswordModal(true)} className="p-2 text-stone hover:text-sage transition-all" title="Change Password">
                <Key size={18} />
              </button>
              <button onClick={onLogout} className="p-2 text-stone hover:text-red-500 transition-all" title="Logout">
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <button 
              onClick={() => onShowAuth?.()} 
              className="bg-sage text-white px-3 py-1.5 rounded-full text-sm font-medium"
            >
              Sign In
            </button>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-charcoal">Change Password 🔐</h2>
              <button onClick={() => setShowPasswordModal(false)} className="text-stone hover:text-charcoal p-2">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-charcoal font-medium mb-2">Current Password</label>
                <input
                  type={showCurrentPw ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 pr-12 outline-none"
                  placeholder="Your current password"
                />
                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-4 top-11 text-stone hover:text-charcoal">
                  {showCurrentPw ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="relative">
                <label className="block text-charcoal font-medium mb-2">New Password</label>
                <input
                  type={showNewPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 pr-12 outline-none"
                  placeholder="New password (min 6 characters)"
                />
                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-4 top-11 text-stone hover:text-charcoal">
                  {showNewPw ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Confirm New Password</label>
                <input
                  type={showNewPw ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none"
                  placeholder="Confirm new password"
                />
              </div>
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="w-full bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50"
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== WELCOME BACKGROUND ====================
const WelcomeBackground = ({ mood }) => {
  const [bgIndex, setBgIndex] = useState(0);
  useEffect(() => { const interval = setInterval(() => { setBgIndex((prev) => (prev + 1) % WELCOME_BACKGROUNDS.length); }, 10000); return () => clearInterval(interval); }, []);
  const bgUrl = mood?.background || WELCOME_BACKGROUNDS[bgIndex];
  const overlay = mood?.overlay || "from-sage/20 to-terracotta/20";

  return (
    <div className="fixed inset-0 z-0 pointer-events-none">
      <img src={bgUrl} alt="Background" className="w-full h-full object-cover opacity-25 transition-opacity duration-1000" />
      <div className={`absolute inset-0 bg-gradient-to-b ${overlay}`} />
      <div className="absolute inset-0 bg-paper/60" />
    </div>
  );
};

// ==================== HOME PAGE ====================
// ==================== DAILY INSPIRATION COMPONENT ====================
const DailyInspiration = () => {
  const [quote, setQuote] = useState(null);
  
  useEffect(() => {
    // Get a consistent quote for the day based on the date
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const quoteIndex = dayOfYear % DAILY_QUOTES.length;
    setQuote(DAILY_QUOTES[quoteIndex]);
  }, []);
  
  if (!quote) return null;
  
  return (
    <div className="bg-gradient-to-br from-sage/10 to-terracotta/10 rounded-3xl p-6 mb-8 border border-sage/20">
      <div className="flex items-start gap-4">
        <div className="text-4xl">{quote.emoji}</div>
        <div className="flex-1">
          <p className="font-handwritten text-xl md:text-2xl text-charcoal leading-relaxed italic">"{quote.quote}"</p>
          <p className="text-sage font-medium mt-2">— {quote.author}</p>
        </div>
      </div>
    </div>
  );
};

const HomePage = ({ stats, setCurrentPage, currentMood, user, songs = [], setInitialSongId, onShowAuth }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Rotating hero images - warm, cozy, pet-friendly scenes
  const heroImages = [
    { url: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200&q=80", caption: "Finding peace in every moment 🐕" },
    { url: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=1200&q=80", caption: "Joy shared is joy doubled 💕" },
    { url: "https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=1200&q=80", caption: "Calm moments, gentle hearts 🌿" },
    { url: "https://images.unsplash.com/photo-1596854407944-bf87f6fdd49e?w=1200&q=80", caption: "Every pet brings harmony 🎵" },
    { url: "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=1200&q=80", caption: "A peaceful place for you & your pets 🏡" },
  ];
  
  // Rotate images every 7 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [heroImages.length]);
  
  // Get "Song of the Day" - based on the current date so it changes daily but stays consistent all day
  const getSongOfTheDay = () => {
    if (!songs || songs.length === 0) return null;
    // Use date to create a consistent "random" pick for the day
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const songIndex = dayOfYear % songs.length;
    return songs[songIndex];
  };
  
  const songOfTheDay = getSongOfTheDay();
  
  return (
    <div data-testid="home-page" className="fade-in relative z-10">
      {/* Daily Inspiration Quote */}
      <DailyInspiration />
      
      {/* Welcome Message */}
      {user && (
        <div className="text-center mb-6">
          <p className="font-handwritten text-xl text-sage">Welcome back, {user.name}! 💕</p>
        </div>
      )}
      
      {currentMood && (
        <div className="mb-8 bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50 text-center">
          <div className="text-5xl mb-2">{currentMood.emoji}</div>
          <h2 className="font-heading text-2xl text-charcoal">Feeling {currentMood.name} today!</h2>
          <p className="text-stone mt-1">{getMoodMessage(currentMood.name)}</p>
        </div>
      )}
      <div className="text-center mb-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">Welcome to Paws & Harmony 🐾</h1>
        <p className="font-handwritten text-2xl md:text-3xl text-sage max-w-2xl mx-auto">A peaceful place to celebrate your pets with songs, photos, and cherished moments 💕</p>
      </div>
      
      {/* Song of the Day - Clean & Simple */}
      {songOfTheDay && (
        <div className="mb-12 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 rounded-3xl p-6 shadow-sm border border-amber-200">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">🎵</span>
            <h2 className="font-heading text-xl font-semibold text-charcoal">Song of the Day</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Song Cover */}
            <div className="w-32 h-32 flex-shrink-0 rounded-2xl overflow-hidden shadow-md bg-gradient-to-br from-sage/20 to-terracotta/20 flex items-center justify-center">
              {songOfTheDay.cover_image || songOfTheDay.cover_thumbnail ? (
                <img 
                  src={songOfTheDay.cover_thumbnail || songOfTheDay.cover_image} 
                  alt={songOfTheDay.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-5xl">🎶</span>
              )}
            </div>
            
            {/* Song Info */}
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading text-2xl font-bold text-charcoal mb-1">{songOfTheDay.title}</h3>
              {songOfTheDay.artist && <p className="text-sage mb-2">by {songOfTheDay.artist}</p>}
              {songOfTheDay.moods && songOfTheDay.moods.length > 0 && (
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3">
                  {songOfTheDay.moods.slice(0, 3).map((tag, i) => (
                    <span key={i} className="text-xs bg-white/70 text-amber-700 px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <button 
                onClick={() => {
                  if (setInitialSongId) setInitialSongId(songOfTheDay.id);
                  setCurrentPage("songbook");
                }}
                className="inline-flex items-center gap-2 bg-amber-500 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-amber-600 transition-all"
              >
                <Play size={16} /> Listen in Songbook
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Rotating Hero Image */}
      <div className="relative rounded-3xl overflow-hidden mb-12 h-64 md:h-80 shadow-xl">
        {heroImages.map((image, index) => (
          <div 
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={image.url} alt={image.caption} className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent flex items-end p-8">
          <p className="text-white font-heading text-2xl md:text-3xl transition-opacity duration-500">
            {heroImages[currentImageIndex].caption}
          </p>
        </div>
        {/* Image indicators */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          {heroImages.map((_, index) => (
            <button 
              key={index} 
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50'}`}
            />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {[
          { key: "pets", icon: "🐕", count: stats.pets, label: "Beloved Pets" },
          { key: "songbook", icon: "🎵", count: stats.songs, label: "Songs" },
          { key: "moments", icon: "📸", count: stats.moments, label: "Moments" },
          { key: "journal", icon: "📔", count: stats.mood_entries || 0, label: "Journal Entries" },
        ].map((item) => (
          <button key={item.key} data-testid={`stats-${item.key}`} onClick={() => setCurrentPage(item.key)} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-white/50">
            <span className="text-3xl mb-2 block">{item.icon}</span>
            <p className="text-3xl font-heading font-bold text-charcoal">{item.count}</p>
            <p className="text-stone">{item.label}</p>
          </button>
        ))}
      </div>
      {user ? (
        <div className="mt-12 text-center">
          <h2 className="font-heading text-2xl font-semibold text-charcoal mb-6">Quick Actions ✨</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <button data-testid="quick-add-pet" onClick={() => setCurrentPage("pets")} className="bg-sage text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-sage-dark transition-all shadow-sm hover:shadow-md flex items-center gap-2"><Plus size={20} />Add Pet 🐾</button>
            {user?.is_admin && <button data-testid="quick-add-song" onClick={() => setCurrentPage("songbook")} className="bg-terracotta text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-terracotta-dark transition-all shadow-sm hover:shadow-md flex items-center gap-2"><Plus size={20} />Add Song 🎶</button>}
            <button data-testid="quick-add-moment" onClick={() => setCurrentPage("moments")} className="bg-sandy text-white px-8 py-4 rounded-full text-lg font-medium hover:brightness-90 transition-all shadow-sm hover:shadow-md flex items-center gap-2"><Plus size={20} />Add Moment 📷</button>
          </div>
        </div>
      ) : (
        <div className="mt-12 text-center bg-gradient-to-r from-sage/10 via-amber-50 to-terracotta/10 rounded-3xl p-8">
          <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4">Create Your Peaceful Space 🏡</h2>
          <p className="text-stone mb-6 max-w-md mx-auto">Sign up free to create pet profiles, journal your memories, and build your own collection of cherished moments.</p>
          <button 
            onClick={() => onShowAuth?.()} 
            className="bg-sage text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-sage-dark transition-all shadow-sm hover:shadow-md"
          >
            Sign Up Free 🐾
          </button>
        </div>
      )}
    </div>
  );
};

const getMoodMessage = (moodName) => {
  const messages = { "Happy": "What a wonderful day! 🌟", "Peaceful": "Enjoy the calm moments. 🍃", "Loving": "Your pets love you! 💕", "Cozy": "Perfect for cuddles. 🧸", "Grateful": "Thankful hearts shine. 🙏", "Relaxed": "Enjoy simple joys. 🌊", "Energetic": "Ready for adventures! 🚀", "Playful": "Time for fun! 🎈", "Gentle": "Soft moments are sweet. 🌷", "Dreamy": "Let imagination wander. ✨", "Pawsitive": "Every day is better with paws! 🐾", "Musical": "Let music fill your heart. 🎼" };
  return messages[moodName] || "Enjoy this moment! ✨";
};

// ==================== PETS PAGE ====================
const PetsPage = ({ pets, fetchPets }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [viewingPet, setViewingPet] = useState(null);
  const [showPrintCard, setShowPrintCard] = useState(false);
  const [formData, setFormData] = useState({ name: "", species: "dog", breed: "", age: "", description: "", photo: "", photos: [], food_type: "", food_brand: "", feeding_times: "", feeding_portions: "", activities: "", favorite_toys: "", medical_info: "", vet_contact: "", special_instructions: "", birthday: "", gotcha_day: "", is_memorial: false, passed_date: "", memorial_message: "" });
  const [loading, setLoading] = useState(false);
  const [viewingPhotoIndex, setViewingPhotoIndex] = useState(0);
  const printRef = useRef(null);

  const speciesOptions = [
    { value: "dog", emoji: "🐕" }, { value: "cat", emoji: "🐱" }, { value: "bird", emoji: "🐦" }, { value: "rabbit", emoji: "🐰" },
    { value: "hamster", emoji: "🐹" }, { value: "fish", emoji: "🐠" }, { value: "turtle", emoji: "🐢" }, { value: "other", emoji: "💕" }
  ];

  const resetForm = () => { setFormData({ name: "", species: "dog", breed: "", age: "", description: "", photo: "", photos: [], food_type: "", food_brand: "", feeding_times: "", feeding_portions: "", activities: "", favorite_toys: "", medical_info: "", vet_contact: "", special_instructions: "", birthday: "", gotcha_day: "", is_memorial: false, passed_date: "", memorial_message: "" }); setEditingPet(null); setShowForm(false); };

  const handleAddPhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photos: [...(formData.photos || []), reader.result] });
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (index) => {
    setFormData({ ...formData, photos: formData.photos.filter((_, i) => i !== index) });
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Pet Care Card - ${viewingPet.name}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; padding: 20px; background: #f9f7f2; }
            .card { max-width: 500px; margin: 0 auto; background: white; border-radius: 24px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
            .header { background: linear-gradient(135deg, #4A6C6F 0%, #E88D67 100%); color: white; padding: 24px; text-align: center; }
            .header h1 { font-family: 'Fraunces', serif; font-size: 28px; margin-bottom: 4px; }
            .header p { opacity: 0.9; font-size: 14px; }
            .photo { width: 120px; height: 120px; border-radius: 50%; margin: -60px auto 16px; border: 4px solid white; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 48px; overflow: hidden; }
            .photo img { width: 100%; height: 100%; object-fit: cover; }
            .content { padding: 24px; padding-top: 8px; }
            .section { margin-bottom: 16px; padding: 16px; background: #f9f7f2; border-radius: 12px; }
            .section h3 { font-family: 'Fraunces', serif; font-size: 16px; color: #4A6C6F; margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
            .section p { font-size: 13px; color: #555; margin-bottom: 4px; line-height: 1.5; }
            .footer { text-align: center; padding: 16px; background: #f9f7f2; font-size: 12px; color: #888; border-top: 1px solid #eee; }
            .emergency { background: #fee2e2; border: 2px solid #f87171; }
            .emergency h3 { color: #dc2626; }
            @media print { body { padding: 0; background: white; } .card { box-shadow: none; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>window.onload = function() { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Please enter a pet name"); return; }
    setLoading(true);
    try {
      if (editingPet) { await axiosAuth.put(`${API}/pets/${editingPet.id}`, formData); toast.success("Pet updated! 🎉"); }
      else { await axiosAuth.post(`${API}/pets`, formData); toast.success("Pet added! 🐾"); }
      await fetchPets(); resetForm();
    } catch (error) { toast.error("Failed to save pet"); }
    finally { setLoading(false); }
  };

  const handleEdit = (pet) => { setFormData({ ...pet }); setEditingPet(pet); setShowForm(true); };
  const handleDelete = async (petId) => { if (!window.confirm("Delete this pet?")) return; try { await axiosAuth.delete(`${API}/pets/${petId}`); toast.success("Pet deleted"); await fetchPets(); } catch (error) { toast.error("Failed to delete"); } };
  const handleImageUpload = (e) => { const file = e.target.files[0]; if (file && file.size <= 5 * 1024 * 1024) { const reader = new FileReader(); reader.onloadend = () => setFormData({ ...formData, photo: reader.result }); reader.readAsDataURL(file); } else { toast.error("Image must be < 5MB"); } };
  const getEmoji = (species) => speciesOptions.find(o => o.value === species)?.emoji || "💕";

  // Pet Detail View
  if (viewingPet) {
    return (
      <div data-testid="pet-detail" className="fade-in relative z-10">
        <button onClick={() => setViewingPet(null)} className="flex items-center gap-2 text-sage mb-6 hover:text-sage-dark"><ChevronLeft size={20} />Back to Pets</button>
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg border border-white/50">
          <div className="h-64 bg-gradient-to-br from-sage/20 to-terracotta/20 relative">
            {viewingPet.photo ? <img src={viewingPet.photo} alt={viewingPet.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-8xl">{getEmoji(viewingPet.species)}</div>}
          </div>
          <div className="p-8">
            <h1 className="font-heading text-3xl font-bold text-charcoal mb-2">{viewingPet.name}</h1>
            <p className="text-stone text-lg capitalize mb-4">{getEmoji(viewingPet.species)} {viewingPet.species} {viewingPet.breed && `• ${viewingPet.breed}`}</p>
            {viewingPet.age && <p className="text-charcoal mb-2">🎂 Age: {viewingPet.age}</p>}
            {viewingPet.birthday && <p className="text-charcoal mb-2">🎈 Birthday: {viewingPet.birthday}</p>}
            {viewingPet.gotcha_day && <p className="text-charcoal mb-2">💕 Gotcha Day: {viewingPet.gotcha_day}</p>}
            {viewingPet.description && <p className="text-stone mt-4">{viewingPet.description}</p>}
            
            {/* Pet Care Guide for Sitters */}
            {(viewingPet.food_type || viewingPet.feeding_times || viewingPet.activities || viewingPet.special_instructions) && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h2 className="font-heading text-2xl font-semibold text-charcoal mb-6 flex items-center gap-2">📋 Pet Care Guide for Sitters</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {(viewingPet.food_type || viewingPet.food_brand || viewingPet.feeding_times || viewingPet.feeding_portions) && (
                    <div className="bg-sage/5 rounded-2xl p-5">
                      <h3 className="font-semibold text-charcoal mb-3 flex items-center gap-2"><Utensils size={18} className="text-sage" />Feeding</h3>
                      {viewingPet.food_type && <p className="text-stone mb-1">🍽️ Food: {viewingPet.food_type}</p>}
                      {viewingPet.food_brand && <p className="text-stone mb-1">🏷️ Brand: {viewingPet.food_brand}</p>}
                      {viewingPet.feeding_times && <p className="text-stone mb-1">⏰ Times: {viewingPet.feeding_times}</p>}
                      {viewingPet.feeding_portions && <p className="text-stone">📏 Portions: {viewingPet.feeding_portions}</p>}
                    </div>
                  )}
                  {(viewingPet.activities || viewingPet.favorite_toys) && (
                    <div className="bg-terracotta/5 rounded-2xl p-5">
                      <h3 className="font-semibold text-charcoal mb-3 flex items-center gap-2"><Gamepad2 size={18} className="text-terracotta" />Activities & Toys</h3>
                      {viewingPet.activities && <p className="text-stone mb-1">🏃 Activities: {viewingPet.activities}</p>}
                      {viewingPet.favorite_toys && <p className="text-stone">🧸 Favorite Toys: {viewingPet.favorite_toys}</p>}
                    </div>
                  )}
                  {(viewingPet.medical_info || viewingPet.vet_contact) && (
                    <div className="bg-blue-50 rounded-2xl p-5">
                      <h3 className="font-semibold text-charcoal mb-3 flex items-center gap-2"><Stethoscope size={18} className="text-blue-500" />Medical Info</h3>
                      {viewingPet.medical_info && <p className="text-stone mb-1">💊 Info: {viewingPet.medical_info}</p>}
                      {viewingPet.vet_contact && <p className="text-stone">🏥 Vet: {viewingPet.vet_contact}</p>}
                    </div>
                  )}
                  {viewingPet.special_instructions && (
                    <div className="bg-yellow-50 rounded-2xl p-5 md:col-span-2">
                      <h3 className="font-semibold text-charcoal mb-3 flex items-center gap-2"><Star size={18} className="text-yellow-500" />Special Instructions</h3>
                      <p className="text-stone whitespace-pre-wrap">{viewingPet.special_instructions}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-3 mt-8">
              <button onClick={() => setShowPrintCard(true)} className="px-6 py-3 bg-terracotta/10 text-terracotta rounded-full font-medium hover:bg-terracotta/20 transition-all flex items-center justify-center gap-2"><Printer size={18} />Print Card</button>
              <button onClick={() => { handleEdit(viewingPet); setViewingPet(null); }} className="flex-1 bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all flex items-center justify-center gap-2"><Edit2 size={18} />Edit</button>
              <button onClick={() => { handleDelete(viewingPet.id); setViewingPet(null); }} className="px-6 py-3 bg-red-50 text-red-500 rounded-full font-medium hover:bg-red-100 transition-all flex items-center justify-center gap-2"><Trash2 size={18} />Delete</button>
            </div>
          </div>
        </div>

        {/* Print Card Modal */}
        {showPrintCard && (
          <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
            <div className="bg-paper rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold text-charcoal">Pet Care Card Preview 🖨️</h2>
                <button onClick={() => setShowPrintCard(false)} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
              </div>
              
              {/* Printable Card */}
              <div ref={printRef}>
                <div className="card">
                  <div className="header" style={{background: 'linear-gradient(135deg, #4A6C6F 0%, #E88D67 100%)', color: 'white', padding: '24px', textAlign: 'center'}}>
                    <h1 style={{fontFamily: 'Fraunces, serif', fontSize: '28px', marginBottom: '4px'}}>{viewingPet.name}</h1>
                    <p style={{opacity: 0.9, fontSize: '14px'}}>{getEmoji(viewingPet.species)} {viewingPet.species} {viewingPet.breed && `• ${viewingPet.breed}`} {viewingPet.age && `• ${viewingPet.age}`}</p>
                  </div>
                  <div style={{textAlign: 'center'}}>
                    <div className="photo" style={{width: '100px', height: '100px', borderRadius: '50%', margin: '-50px auto 12px', border: '4px solid white', background: viewingPet.photo ? 'transparent' : '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', overflow: 'hidden'}}>
                      {viewingPet.photo ? <img src={viewingPet.photo} alt={viewingPet.name} style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : getEmoji(viewingPet.species)}
                    </div>
                  </div>
                  <div style={{padding: '20px', paddingTop: '8px'}}>
                    {(viewingPet.food_type || viewingPet.feeding_times) && (
                      <div style={{marginBottom: '12px', padding: '12px', background: '#f0fdf4', borderRadius: '12px'}}>
                        <h3 style={{fontSize: '14px', color: '#4A6C6F', marginBottom: '6px'}}>🍽️ Feeding</h3>
                        {viewingPet.food_type && <p style={{fontSize: '12px', color: '#555', marginBottom: '2px'}}>Food: {viewingPet.food_type}</p>}
                        {viewingPet.food_brand && <p style={{fontSize: '12px', color: '#555', marginBottom: '2px'}}>Brand: {viewingPet.food_brand}</p>}
                        {viewingPet.feeding_times && <p style={{fontSize: '12px', color: '#555', marginBottom: '2px'}}>Times: {viewingPet.feeding_times}</p>}
                        {viewingPet.feeding_portions && <p style={{fontSize: '12px', color: '#555'}}>Portions: {viewingPet.feeding_portions}</p>}
                      </div>
                    )}
                    {(viewingPet.activities || viewingPet.favorite_toys) && (
                      <div style={{marginBottom: '12px', padding: '12px', background: '#fef3c7', borderRadius: '12px'}}>
                        <h3 style={{fontSize: '14px', color: '#d97706', marginBottom: '6px'}}>🎾 Activities & Toys</h3>
                        {viewingPet.activities && <p style={{fontSize: '12px', color: '#555', marginBottom: '2px'}}>Activities: {viewingPet.activities}</p>}
                        {viewingPet.favorite_toys && <p style={{fontSize: '12px', color: '#555'}}>Toys: {viewingPet.favorite_toys}</p>}
                      </div>
                    )}
                    {(viewingPet.medical_info || viewingPet.vet_contact) && (
                      <div style={{marginBottom: '12px', padding: '12px', background: '#fee2e2', borderRadius: '12px', border: '2px solid #f87171'}}>
                        <h3 style={{fontSize: '14px', color: '#dc2626', marginBottom: '6px'}}>🏥 Medical (Important!)</h3>
                        {viewingPet.medical_info && <p style={{fontSize: '12px', color: '#555', marginBottom: '2px'}}>Info: {viewingPet.medical_info}</p>}
                        {viewingPet.vet_contact && <p style={{fontSize: '12px', color: '#555'}}>Vet Contact: {viewingPet.vet_contact}</p>}
                      </div>
                    )}
                    {viewingPet.special_instructions && (
                      <div style={{marginBottom: '12px', padding: '12px', background: '#e0e7ff', borderRadius: '12px'}}>
                        <h3 style={{fontSize: '14px', color: '#4338ca', marginBottom: '6px'}}>⭐ Special Notes</h3>
                        <p style={{fontSize: '12px', color: '#555', whiteSpace: 'pre-wrap'}}>{viewingPet.special_instructions}</p>
                      </div>
                    )}
                    <div style={{textAlign: 'center', paddingTop: '8px', borderTop: '1px solid #eee', marginTop: '8px'}}>
                      <p style={{fontSize: '11px', color: '#888'}}>Made with 💕 by Paws & Harmony</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={handlePrint} className="flex-1 bg-terracotta text-white py-3 rounded-full font-medium hover:bg-terracotta-dark transition-all flex items-center justify-center gap-2"><Printer size={18} />Print Card</button>
                <button onClick={() => setShowPrintCard(false)} className="px-6 py-3 bg-gray-100 text-charcoal rounded-full font-medium hover:bg-gray-200 transition-all">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="pets-page" className="fade-in relative z-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal">Your Beloved Pets 🐾</h1>
        <button data-testid="add-pet-button" onClick={() => setShowForm(true)} className="bg-sage text-white px-6 py-3 rounded-full text-lg font-medium hover:bg-sage-dark transition-all flex items-center gap-2"><Plus size={20} />Add Pet</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-charcoal">{editingPet ? "Edit Pet 🐾" : "Add New Pet 🐾"}</h2>
              <button onClick={resetForm} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-charcoal font-medium mb-2">Pet Name *</label>
                  <input data-testid="pet-name-input" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Name" required />
                </div>
                <div>
                  <label className="block text-charcoal font-medium mb-2">Breed</label>
                  <input type="text" value={formData.breed} onChange={(e) => setFormData({ ...formData, breed: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Breed" />
                </div>
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Species</label>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                  {speciesOptions.map((opt) => (
                    <button key={opt.value} type="button" onClick={() => setFormData({ ...formData, species: opt.value })} className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${formData.species === opt.value ? "border-sage bg-sage/10" : "border-gray-200"}`}>
                      <span className="text-xl">{opt.emoji}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-charcoal font-medium mb-2">Age</label><input type="text" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., 3 years" /></div>
                <div><label className="block text-charcoal font-medium mb-2">Birthday 🎂</label><input type="date" value={formData.birthday} onChange={(e) => setFormData({ ...formData, birthday: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" /></div>
                <div><label className="block text-charcoal font-medium mb-2">Gotcha Day 💕</label><input type="date" value={formData.gotcha_day} onChange={(e) => setFormData({ ...formData, gotcha_day: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" /></div>
              </div>
              <div><label className="block text-charcoal font-medium mb-2">Description</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full h-20 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none" placeholder="About your pet..." /></div>
              <div><label className="block text-charcoal font-medium mb-2">Main Photo 📷</label>
                <div className="flex items-center gap-4">
                  {formData.photo ? <div className="relative"><img src={formData.photo} alt="Pet" className="w-20 h-20 rounded-xl object-cover" /><button type="button" onClick={() => setFormData({ ...formData, photo: "" })} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={14} /></button></div> : <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center text-3xl">📷</div>}
                  <label className="cursor-pointer bg-paper text-sage border-2 border-sage/20 hover:border-sage px-4 py-2 rounded-full transition-all"><input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />Choose Main Photo</label>
                </div>
              </div>

              <div><label className="block text-charcoal font-medium mb-2">Photo Gallery 🖼️ <span className="text-stone text-sm font-normal">(add more photos)</span></label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(formData.photos || []).map((photo, index) => (
                    <div key={index} className="relative">
                      <img src={photo} alt={`Gallery ${index + 1}`} className="w-16 h-16 rounded-lg object-cover" />
                      <button type="button" onClick={() => removePhoto(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={12} /></button>
                    </div>
                  ))}
                  <label className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all">
                    <input type="file" accept="image/*" onChange={handleAddPhoto} className="hidden" />
                    <Plus size={24} className="text-stone" />
                  </label>
                </div>
                <p className="text-stone text-xs">Add photos of your pet doing their favorite things!</p>
              </div>

              {/* Pet Care Info */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="font-heading text-lg font-semibold text-charcoal mb-4">📋 Pet Care Info (for sitters)</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-charcoal font-medium mb-2">Food Type 🍽️</label><input type="text" value={formData.food_type} onChange={(e) => setFormData({ ...formData, food_type: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Dry kibble" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Food Brand 🏷️</label><input type="text" value={formData.food_brand} onChange={(e) => setFormData({ ...formData, food_brand: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Blue Buffalo" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Feeding Times ⏰</label><input type="text" value={formData.feeding_times} onChange={(e) => setFormData({ ...formData, feeding_times: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., 7am and 6pm" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Portions 📏</label><input type="text" value={formData.feeding_portions} onChange={(e) => setFormData({ ...formData, feeding_portions: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., 1 cup per meal" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Activities 🏃</label><input type="text" value={formData.activities} onChange={(e) => setFormData({ ...formData, activities: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., 2 walks daily, loves fetch" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Favorite Toys 🧸</label><input type="text" value={formData.favorite_toys} onChange={(e) => setFormData({ ...formData, favorite_toys: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Tennis ball, squeaky toy" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Medical Info 💊</label><input type="text" value={formData.medical_info} onChange={(e) => setFormData({ ...formData, medical_info: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Takes medication at 8am" /></div>
                  <div><label className="block text-charcoal font-medium mb-2">Vet Contact 🏥</label><input type="text" value={formData.vet_contact} onChange={(e) => setFormData({ ...formData, vet_contact: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="e.g., Dr. Smith 555-1234" /></div>
                </div>
                <div className="mt-4"><label className="block text-charcoal font-medium mb-2">Special Instructions ⭐</label><textarea value={formData.special_instructions} onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })} className="w-full h-24 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none" placeholder="e.g., May not want to eat in the morning - that's OK. Give treats after walks..." /></div>
              </div>

              {/* Memorial Section */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <input 
                    type="checkbox" 
                    id="is_memorial" 
                    checked={formData.is_memorial} 
                    onChange={(e) => setFormData({ ...formData, is_memorial: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="is_memorial" className="font-heading text-lg font-semibold text-charcoal">🕯️ This pet has crossed the rainbow bridge</label>
                </div>
                {formData.is_memorial && (
                  <div className="bg-purple-50 rounded-2xl p-4 space-y-4">
                    <p className="text-purple-700 text-sm">Create a loving memorial for your cherished companion 💕</p>
                    <div><label className="block text-charcoal font-medium mb-2">Date They Passed 🌈</label><input type="date" value={formData.passed_date} onChange={(e) => setFormData({ ...formData, passed_date: e.target.value })} className="w-full h-12 border-2 border-purple-200 focus:border-purple-400 rounded-xl px-4 outline-none" /></div>
                    <div><label className="block text-charcoal font-medium mb-2">Memorial Message 💝</label><textarea value={formData.memorial_message} onChange={(e) => setFormData({ ...formData, memorial_message: e.target.value })} className="w-full h-24 border-2 border-purple-200 focus:border-purple-400 rounded-xl px-4 py-2 outline-none resize-none" placeholder="Share a loving memory or message about your pet..." /></div>
                  </div>
                )}
              </div>

              <button data-testid="submit-pet-button" type="submit" disabled={loading} className="w-full bg-sage text-white py-4 rounded-full text-lg font-medium hover:bg-sage-dark transition-all disabled:opacity-50">{loading ? "Saving..." : editingPet ? "Update Pet 🐾" : "Add Pet 🐾"}</button>
              <button type="button" onClick={resetForm} className="w-full bg-gray-100 text-charcoal py-3 rounded-full font-medium hover:bg-gray-200 transition-all mt-2">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {pets.length === 0 ? (
        <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50"><span className="text-5xl block mb-4">🐾</span><h3 className="font-heading text-xl text-charcoal mb-2">No pets yet</h3><p className="text-stone mb-6">Add your first pet!</p><button onClick={() => setShowForm(true)} className="bg-sage text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-sage-dark transition-all">Add Your First Pet 🐾</button></div>
      ) : (
        <>
          {/* Living Pets */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.filter(p => !p.is_memorial).map((pet) => (
              <div key={pet.id} data-testid={`pet-card-${pet.id}`} onClick={() => setViewingPet(pet)} className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer border border-white/50 hover:-translate-y-1">
                <div className="h-48 bg-gradient-to-br from-sage/20 to-terracotta/20 relative">{pet.photo ? <img src={pet.photo} alt={pet.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-6xl">{getEmoji(pet.species)}</div>}</div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-charcoal">{pet.name}</h3>
                  <p className="text-stone capitalize">{getEmoji(pet.species)} {pet.species}{pet.breed ? ` • ${pet.breed}` : ""}</p>
                  {pet.age && <p className="text-stone text-sm mt-1">🎂 {pet.age}</p>}
                  {pet.special_instructions && <p className="text-sage text-sm mt-2 flex items-center gap-1"><Star size={14} />Has care instructions</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Memorial Section */}
          {pets.some(p => p.is_memorial) && (
            <div className="mt-12">
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-semibold text-charcoal flex items-center justify-center gap-3">
                  <span className="text-3xl">🕯️</span> Forever in Our Hearts <span className="text-3xl">🌈</span>
                </h2>
                <p className="text-stone mt-2 font-handwritten text-xl">Cherished companions who crossed the rainbow bridge 💕</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pets.filter(p => p.is_memorial).map((pet) => (
                  <div key={pet.id} data-testid={`memorial-card-${pet.id}`} onClick={() => setViewingPet(pet)} className="bg-gradient-to-br from-purple-50 to-indigo-50 backdrop-blur-sm rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer border border-purple-200 hover:-translate-y-1">
                    <div className="h-48 bg-gradient-to-br from-purple-100 to-indigo-100 relative">
                      {pet.photo ? <img src={pet.photo} alt={pet.name} className="w-full h-full object-cover opacity-90" /> : <div className="w-full h-full flex items-center justify-center text-6xl">{getEmoji(pet.species)}</div>}
                      <div className="absolute top-3 right-3 bg-white/90 p-2 rounded-full"><span className="text-lg">🌈</span></div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-semibold text-charcoal">{pet.name}</h3>
                      <p className="text-stone capitalize">{getEmoji(pet.species)} {pet.species}{pet.breed ? ` • ${pet.breed}` : ""}</p>
                      {pet.passed_date && <p className="text-purple-600 text-sm mt-1">🕯️ {new Date(pet.passed_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>}
                      {pet.memorial_message && <p className="text-stone text-sm mt-2 italic line-clamp-2">"{pet.memorial_message}"</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== SONGBOOK PAGE (STORYBOOK STYLE) ====================
const SongbookPage = ({ songs, fetchSongs, categories, pets, isAdmin, initialSongId, clearInitialSong, collections }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [viewingSong, setViewingSong] = useState(null);
  const [currentIllustration, setCurrentIllustration] = useState(0);
  const [formData, setFormData] = useState({ title: "", artist: "", cover_image: "", audio_file: "", audio_filename: "", song_type: "", category_id: "", pet_id: "", notes: "", lyrics: "", illustrations: [], story_text: "", is_favorite: false, moods: [] });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedMoodFilter, setSelectedMoodFilter] = useState(null);
  const [selectedCollectionFilter, setSelectedCollectionFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSong, setLoadingSong] = useState(false);
  const [generatingThumbs, setGeneratingThumbs] = useState(false);
  const [sortBy, setSortBy] = useState("alpha"); // Changed default to alphabetical
  const [viewMode, setViewMode] = useState("storybook"); // "storybook" or "album"
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedLetter, setSelectedLetter] = useState(null); // For A-Z filter

  // Save scroll position before viewing a song
  const handleViewSong = async (song) => {
    setScrollPosition(window.scrollY);
    await viewSong(song);
  };

  // Restore scroll position when closing song view
  const handleCloseSong = () => {
    setViewingSong(null);
    setCurrentIllustration(0);
    setLinkCopied(false);
    // Use setTimeout to restore scroll after the view updates
    setTimeout(() => {
      window.scrollTo({ top: scrollPosition, behavior: 'instant' });
    }, 50);
  };

  // Copy shareable song link to clipboard
  const copyShareLink = (songId) => {
    const shareUrl = `${window.location.origin}?song=${songId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      toast.success("Link copied! Share it on Facebook, Twitter, or anywhere 🎵");
      setTimeout(() => setLinkCopied(false), 3000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setLinkCopied(true);
      toast.success("Link copied! Share it on Facebook, Twitter, or anywhere 🎵");
      setTimeout(() => setLinkCopied(false), 3000);
    });
  };

  const songTypeOptions = [{ value: "pets", label: "For Pets", emoji: "🐾" }, { value: "people", label: "For People", emoji: "👨‍👩‍👧" }, { value: "rescues", label: "For Rescues", emoji: "🏠" }];
  const moodOptions = ["Calm", "Uplifting", "Comfort", "Playful", "Sleepy", "Healing", "Joyful", "Peaceful", "Energetic", "Soothing", "Big Bear Eagles"];

  // Check if thumbnails need to be generated (songs have cover but no thumbnail yet)
  // Show button if admin and there are any songs (admin can regenerate if needed)
  const needsThumbnails = isAdmin && songs.length > 0;

  // Search songs
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/songs/search/${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  // Generate thumbnails for all songs
  const generateThumbnails = async () => {
    setGeneratingThumbs(true);
    try {
      const response = await axiosAuth.post(`${API}/songs/generate-thumbnails`);
      toast.success(response.data.message || "Thumbnails generated! Refreshing...");
      await fetchSongs();
    } catch (e) {
      toast.error("Failed to generate thumbnails - try again or check if songs have cover images");
    } finally {
      setGeneratingThumbs(false);
    }
  };

  // Fetch full song data (including audio) when viewing
  const viewSong = async (song) => {
    // If song already has audio loaded or doesn't have audio, show immediately
    if (song.audio_file || !song.audio_file_id) {
      setViewingSong(song);
      return;
    }
    // Fetch audio separately to avoid loading large payloads with song metadata
    setLoadingSong(true);
    try {
      // First get full song metadata (without audio)
      const songResponse = await axios.get(`${API}/songs/${song.id}`);
      const songData = songResponse.data;
      
      // Then fetch audio separately
      try {
        const audioResponse = await axios.get(`${API}/songs/${song.id}/audio`);
        if (audioResponse.data.audio_file) {
          songData.audio_file = audioResponse.data.audio_file;
        }
      } catch (audioErr) {
        console.log("Audio not available for this song");
      }
      
      setViewingSong(songData);
    } catch (e) {
      // Fallback to showing song without audio
      setViewingSong(song);
    } finally {
      setLoadingSong(false);
    }
  };

  // Handle Song of the Day direct link - open the song detail view directly
  useEffect(() => {
    if (initialSongId && songs.length > 0) {
      const song = songs.find(s => s.id === initialSongId);
      if (song) {
        viewSong(song);
        // Clean up URL after successfully opening the song
        window.history.replaceState({}, document.title, window.location.pathname);
        if (clearInitialSong) clearInitialSong();
      }
    }
  }, [initialSongId, songs]);

  const resetForm = () => { setFormData({ title: "", artist: "", cover_image: "", audio_file: "", audio_filename: "", song_type: "", category_id: "", pet_id: "", notes: "", lyrics: "", illustrations: [], story_text: "", is_favorite: false, moods: [] }); setEditingSong(null); setShowForm(false); };

  const toggleFavorite = async (songId, currentStatus) => {
    try {
      await axiosAuth.post(`${API}/songs/${songId}/favorite`);
      await fetchSongs();
      toast.success(currentStatus ? "Removed from favorites" : "Added to favorites! ⭐");
    } catch (error) {
      const msg = error.response?.status === 403 ? "Admin access required" : "Failed to update";
      toast.error(msg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error("Please enter a song title"); return; }
    setLoading(true);
    
    // Create a clean payload with all fields matching SongCreate Pydantic model
    const payload = {
      title: formData.title,
      artist: formData.artist || null,
      lyrics: formData.lyrics || null,
      song_type: formData.song_type || null,
      moods: formData.moods || [],  // Changed from mood_tags to match backend
      notes: formData.notes || null,
      cover_image: formData.cover_image || null,
      cover_thumbnail: formData.cover_thumbnail || null,
      audio_file: formData.audio_file || null,
      audio_file_id: formData.audio_file_id || null,
      audio_filename: formData.audio_filename || null,
      category_id: formData.category_id || null,
      pet_id: formData.pet_id || null,
      story_text: formData.story_text || null,
      is_favorite: formData.is_favorite || false,
      illustrations: formData.illustrations || []
    };
    
    // Helper function with retry
    const saveWithRetry = async (retries = 2) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          if (editingSong) { 
            await axiosAuth.put(`${API}/songs/${editingSong.id}`, payload, { timeout: 30000 }); 
          } else { 
            await axiosAuth.post(`${API}/songs`, payload, { timeout: 30000 }); 
          }
          return true; // Success
        } catch (error) {
          console.error(`Song save attempt ${attempt} failed:`, error);
          if (attempt === retries) throw error; // Re-throw on final attempt
          await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
        }
      }
    };
    
    try {
      await saveWithRetry(2);
      toast.success(editingSong ? "Song updated! 🎵" : "Song added! 🎶");
      await fetchSongs(); 
      resetForm();
    } catch (error) { 
      console.error("Song save error after retries:", error);
      let msg = "Failed to save song";
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        msg = "Request timed out. The song may have saved - please refresh to check.";
      } else if (error.response?.status === 403) {
        msg = "Admin access required";
      } else if (error.response?.status === 413) {
        msg = "File too large. Please use smaller images/audio.";
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg); 
    }
    finally { setLoading(false); }
  };

  const handleEdit = (song) => { setFormData({ ...song, illustrations: song.illustrations || [] }); setEditingSong(song); setShowForm(true); };
  const handleDelete = async (songId) => { if (!window.confirm("Delete this song?")) return; try { await axiosAuth.delete(`${API}/songs/${songId}`); toast.success("Song deleted"); await fetchSongs(); setViewingSong(null); } catch (error) { const msg = error.response?.status === 403 ? "Admin access required" : "Failed to delete"; toast.error(msg); } };
  
  // Compress image to reduce payload size
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  
  const handleImageUpload = async (e, field) => { 
    const file = e.target.files[0]; 
    if (file && file.size <= 10 * 1024 * 1024) { 
      try {
        const compressedImage = await compressImage(file);
        if (field === "illustrations") { 
          setFormData({ ...formData, illustrations: [...formData.illustrations, compressedImage] }); 
        } else { 
          setFormData({ ...formData, [field]: compressedImage }); 
        }
        toast.success("Image added!");
      } catch (err) {
        toast.error("Failed to process image");
      }
    } else {
      toast.error("Image too large (max 10MB)");
    }
  };
  const handleAudioUpload = (e) => { const file = e.target.files[0]; if (file && file.size <= 15 * 1024 * 1024) { const reader = new FileReader(); reader.onloadend = () => setFormData({ ...formData, audio_file: reader.result, audio_filename: file.name }); reader.readAsDataURL(file); } };
  const removeIllustration = (index) => { setFormData({ ...formData, illustrations: formData.illustrations.filter((_, i) => i !== index) }); };
  const toggleMood = (mood) => {
    const currentMoods = formData.moods || [];
    if (currentMoods.includes(mood)) {
      setFormData({ ...formData, moods: currentMoods.filter(m => m !== mood) });
    } else {
      setFormData({ ...formData, moods: [...currentMoods, mood] });
    }
  };

  // Use search results if available, otherwise use all songs
  const baseSongs = searchResults !== null ? searchResults : songs;
  // Apply favorites filter
  const afterFavoritesFilter = showFavoritesOnly ? baseSongs.filter(s => s.is_favorite) : baseSongs;
  // Apply mood filter
  const afterMoodFilter = selectedMoodFilter 
    ? afterFavoritesFilter.filter(s => s.moods && s.moods.includes(selectedMoodFilter))
    : afterFavoritesFilter;
  // Apply collection filter
  const filteredSongs = selectedCollectionFilter
    ? afterMoodFilter.filter(s => s.collection_ids && s.collection_ids.includes(selectedCollectionFilter))
    : afterMoodFilter;
  const sortedSongs = [...filteredSongs].sort((a, b) => {
    if (sortBy === "newest") return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === "oldest") return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === "title" || sortBy === "alpha") return a.title.localeCompare(b.title);
    return 0;
  });

  // Filter by selected letter
  const filteredByLetter = selectedLetter 
    ? sortedSongs.filter(song => song.title.toUpperCase().startsWith(selectedLetter))
    : sortedSongs;

  // Get unique first letters for A-Z navigation
  const uniqueLetters = [...new Set(songs.map(s => s.title[0]?.toUpperCase()).filter(Boolean))].sort();

  // Loading state for fetching a single song
  if (loadingSong) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-5xl animate-bounce mb-4">🎵</div>
          <p className="text-stone">Loading song...</p>
        </div>
      </div>
    );
  }

  // Storybook View for a Song
  if (viewingSong) {
    const hasIllustrations = viewingSong.illustrations && viewingSong.illustrations.length > 0;
    return (
      <div data-testid="songbook-detail" className="fade-in relative z-10">
        <div className="flex items-center justify-between mb-6">
          <button onClick={handleCloseSong} className="flex items-center gap-2 text-sage hover:text-sage-dark"><ChevronLeft size={20} />Back to Songbook</button>
          {/* View Mode Toggle + Share Button */}
          <div className="flex items-center gap-3">
            <button 
              data-testid="share-song-btn"
              onClick={() => copyShareLink(viewingSong.id)} 
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${linkCopied ? "bg-green-500 text-white" : "bg-terracotta/10 text-terracotta hover:bg-terracotta/20"}`}
            >
              {linkCopied ? <><Check size={16} />Link Copied!</> : <><Share2 size={16} />Share Song</>}
            </button>
            <div className="flex items-center gap-2 bg-white/90 rounded-full p-1">
              <button onClick={() => setViewMode("storybook")} className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${viewMode === "storybook" ? "bg-sage text-white" : "text-stone hover:text-charcoal"}`}><BookOpen size={16} />Storybook</button>
              <button onClick={() => setViewMode("album")} className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${viewMode === "album" ? "bg-sage text-white" : "text-stone hover:text-charcoal"}`}><LayoutGrid size={16} />Album</button>
            </div>
          </div>
        </div>
        
        {viewMode === "storybook" ? (
          /* STORYBOOK VIEW - Illustrations surround the content like Disney/Joni Mitchell */
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden">
            {/* Storybook Header */}
            <div className="relative h-80 bg-gradient-to-br from-terracotta/30 to-sandy/30">
              {viewingSong.cover_image ? <img src={viewingSong.cover_image} alt={viewingSong.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-8xl">🎵</div>}
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h1 className="font-heading text-4xl font-bold mb-2">{viewingSong.title}</h1>
                {viewingSong.artist && <p className="text-xl opacity-90">🎤 {viewingSong.artist}</p>}
                {viewingSong.song_type && <span className="inline-block mt-3 px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">{songTypeOptions.find(o => o.value === viewingSong.song_type)?.emoji} {viewingSong.song_type}</span>}
              </div>
            </div>

            {/* Audio Player */}
            {viewingSong.audio_file && (
              <div className="p-6 bg-sage/5 border-b border-gray-100">
                <AudioPlayer audioFile={viewingSong.audio_file} title={viewingSong.title} />
              </div>
            )}

            {/* Combined Storybook Page - Illustrations + Lyrics Together */}
            {(hasIllustrations || viewingSong.lyrics) && (
              <div className="p-8 border-b border-gray-100">
                <div className="max-w-4xl mx-auto">
                  {hasIllustrations && viewingSong.lyrics ? (
                    /* Side by side layout like a storybook spread */
                    <div className="grid md:grid-cols-2 gap-8 items-start">
                      <div>
                        <div className="aspect-square rounded-2xl overflow-hidden shadow-lg border-4 border-paper">
                          <img src={viewingSong.illustrations[currentIllustration]} alt={`Illustration ${currentIllustration + 1}`} className="w-full h-full object-contain bg-paper" />
                        </div>
                        {viewingSong.illustrations.length > 1 && (
                          <div className="flex justify-center items-center gap-6 mt-6">
                            <button onClick={() => setCurrentIllustration((prev) => (prev - 1 + viewingSong.illustrations.length) % viewingSong.illustrations.length)} className="p-3 bg-terracotta text-white rounded-full hover:bg-terracotta-dark transition-all shadow-lg hover:shadow-xl transform hover:scale-110"><ChevronLeft size={28} /></button>
                            <span className="text-charcoal font-medium text-lg bg-white/80 px-4 py-2 rounded-full shadow">{currentIllustration + 1} of {viewingSong.illustrations.length}</span>
                            <button onClick={() => setCurrentIllustration((prev) => (prev + 1) % viewingSong.illustrations.length)} className="p-3 bg-terracotta text-white rounded-full hover:bg-terracotta-dark transition-all shadow-lg hover:shadow-xl transform hover:scale-110"><ChevronRight size={28} /></button>
                          </div>
                        )}
                      </div>
                      <div className="bg-paper/80 rounded-2xl p-6 border border-sage/20 shadow-inner">
                        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4 text-center">📜 {viewingSong.title}</h2>
                        <p className="font-handwritten text-xl text-charcoal whitespace-pre-wrap leading-relaxed">{viewingSong.lyrics}</p>
                      </div>
                    </div>
                  ) : hasIllustrations ? (
                    /* Only illustrations */
                    <div>
                      <h2 className="font-heading text-2xl font-semibold text-charcoal mb-6 text-center">✨ Illustrations ✨</h2>
                      <div className="aspect-[4/3] max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-lg border-4 border-white">
                        <img src={viewingSong.illustrations[currentIllustration]} alt="" className="w-full h-full object-contain bg-paper" />
                      </div>
                      {viewingSong.illustrations.length > 1 && (
                        <div className="flex justify-center items-center gap-6 mt-6">
                          <button onClick={() => setCurrentIllustration((prev) => (prev - 1 + viewingSong.illustrations.length) % viewingSong.illustrations.length)} className="p-3 bg-terracotta text-white rounded-full hover:bg-terracotta-dark transition-all shadow-lg hover:shadow-xl transform hover:scale-110"><ChevronLeft size={28} /></button>
                          <span className="text-charcoal font-medium text-lg bg-white/80 px-4 py-2 rounded-full shadow">{currentIllustration + 1} of {viewingSong.illustrations.length}</span>
                          <button onClick={() => setCurrentIllustration((prev) => (prev + 1) % viewingSong.illustrations.length)} className="p-3 bg-terracotta text-white rounded-full hover:bg-terracotta-dark transition-all shadow-lg hover:shadow-xl transform hover:scale-110"><ChevronRight size={28} /></button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Only lyrics */
                    <div className="max-w-2xl mx-auto bg-paper/50 rounded-2xl p-6 border border-sage/20">
                      <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4 text-center">📜 Lyrics</h2>
                      <p className="font-handwritten text-xl text-charcoal whitespace-pre-wrap leading-relaxed">{viewingSong.lyrics}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Story Text */}
            {viewingSong.story_text && (
              <div className="p-8 border-b border-gray-100">
                <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4 text-center">📖 Story</h2>
                <div className="max-w-2xl mx-auto"><p className="text-charcoal leading-relaxed whitespace-pre-wrap">{viewingSong.story_text}</p></div>
              </div>
            )}

            {/* Notes */}
            {viewingSong.notes && (
              <div className="p-8 border-b border-gray-100">
                <h2 className="font-heading text-xl font-semibold text-charcoal mb-3">📝 Notes</h2>
                <p className="text-stone">{viewingSong.notes}</p>
              </div>
            )}

            {/* Actions - Admin only */}
            {isAdmin && (
              <div className="p-6 flex gap-3">
                <button onClick={() => { handleEdit(viewingSong); setViewingSong(null); }} className="flex-1 bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all flex items-center justify-center gap-2"><Edit2 size={18} />Edit</button>
                <button onClick={() => handleDelete(viewingSong.id)} className="px-6 py-3 bg-red-50 text-red-500 rounded-full font-medium hover:bg-red-100 transition-all flex items-center justify-center gap-2"><Trash2 size={18} />Delete</button>
              </div>
            )}
          </div>
        ) : (
          /* ALBUM VIEW - Cover as main image, lyrics in separate tab-like view */
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden">
            <div className="md:flex">
              {/* Album Cover Side */}
              <div className="md:w-1/2 p-8 flex flex-col items-center justify-center bg-gradient-to-br from-terracotta/10 to-sandy/10">
                <div className="w-full max-w-sm aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                  {viewingSong.cover_image ? <img src={viewingSong.cover_image} alt={viewingSong.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-8xl bg-gradient-to-br from-terracotta/30 to-sandy/30">🎵</div>}
                </div>
                <h1 className="font-heading text-3xl font-bold text-charcoal mt-6 text-center">{viewingSong.title}</h1>
                {viewingSong.artist && <p className="text-stone text-lg mt-2">🎤 {viewingSong.artist}</p>}
                {viewingSong.song_type && <span className="mt-3 px-4 py-1 bg-terracotta/10 text-terracotta rounded-full text-sm">{songTypeOptions.find(o => o.value === viewingSong.song_type)?.emoji} {viewingSong.song_type}</span>}
                {viewingSong.audio_file && <div className="w-full mt-6"><AudioPlayer audioFile={viewingSong.audio_file} title={viewingSong.title} /></div>}
              </div>
              
              {/* Content Side */}
              <div className="md:w-1/2 p-8 overflow-y-auto max-h-[600px]">
                {hasIllustrations && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg font-semibold text-charcoal mb-3">🎨 Illustrations</h3>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {viewingSong.illustrations.map((img, idx) => (
                        <img key={idx} src={img} alt="" className="w-24 h-24 rounded-xl object-cover flex-shrink-0 cursor-pointer hover:scale-105 transition-all" onClick={() => setCurrentIllustration(idx)} />
                      ))}
                    </div>
                  </div>
                )}
                {viewingSong.lyrics && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg font-semibold text-charcoal mb-3">📜 Lyrics</h3>
                    <div className="bg-paper/50 rounded-xl p-4 border border-sage/20">
                      <p className="font-handwritten text-lg text-charcoal whitespace-pre-wrap leading-relaxed">{viewingSong.lyrics}</p>
                    </div>
                  </div>
                )}
                {viewingSong.story_text && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg font-semibold text-charcoal mb-3">📖 Story</h3>
                    <p className="text-charcoal whitespace-pre-wrap">{viewingSong.story_text}</p>
                  </div>
                )}
                {viewingSong.notes && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg font-semibold text-charcoal mb-3">📝 Notes</h3>
                    <p className="text-stone">{viewingSong.notes}</p>
                  </div>
                )}
                {isAdmin && (
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button onClick={() => { handleEdit(viewingSong); setViewingSong(null); }} className="flex-1 bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all flex items-center justify-center gap-2"><Edit2 size={18} />Edit</button>
                    <button onClick={() => handleDelete(viewingSong.id)} className="px-6 py-3 bg-red-50 text-red-500 rounded-full font-medium hover:bg-red-100 transition-all flex items-center justify-center gap-2"><Trash2 size={18} />Delete</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="songbook-page" className="fade-in relative z-10">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal">Songbook 🎵</h1>
        <div className="flex items-center gap-3">
          {/* Favorites Filter */}
          <button 
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`h-12 px-4 rounded-full font-medium transition-all flex items-center gap-2 ${showFavoritesOnly ? 'bg-amber-100 text-amber-700 border-2 border-amber-300' : 'bg-white border-2 border-gray-200 text-stone hover:border-amber-300'}`}
          >
            <Star size={18} className={showFavoritesOnly ? 'fill-amber-500' : ''} />
            Favorites
          </button>
          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setSelectedLetter(null); }} className="h-12 px-4 border-2 border-gray-200 rounded-full bg-white focus:border-sage outline-none">
            <option value="alpha">A-Z</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
          {isAdmin && <button data-testid="add-song-button" onClick={() => setShowForm(true)} className="bg-terracotta text-white px-6 py-3 rounded-full font-medium hover:bg-terracotta-dark transition-all flex items-center gap-2"><Plus size={20} />Add Song</button>}
          {needsThumbnails && (
            <button 
              onClick={generateThumbnails} 
              disabled={generatingThumbs}
              className="bg-sage text-white px-6 py-3 rounded-full font-medium hover:bg-sage/80 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {generatingThumbs ? "Generating..." : "🖼️ Generate Thumbnails"}
            </button>
          )}
        </div>
      </div>

      {/* A-Z Letter Filter - Show when sorted alphabetically */}
      {sortBy === "alpha" && (
        <div className="mb-6 flex flex-wrap gap-1 justify-center">
          <button
            onClick={() => setSelectedLetter(null)}
            className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${!selectedLetter ? 'bg-sage text-white' : 'bg-white text-charcoal hover:bg-sage/20'}`}
          >
            All
          </button>
          {uniqueLetters.map(letter => (
            <button
              key={letter}
              onClick={() => setSelectedLetter(letter)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${selectedLetter === letter ? 'bg-sage text-white' : 'bg-white text-charcoal hover:bg-sage/20'}`}
            >
              {letter}
            </button>
          ))}
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by title, artist, lyrics, or mood..."
              className="w-full h-12 pl-12 pr-4 border-2 border-gray-200 focus:border-sage rounded-full bg-white outline-none"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={searching}
            className="h-12 px-6 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50"
          >
            {searching ? "..." : "Search"}
          </button>
          {searchResults !== null && (
            <button
              onClick={clearSearch}
              className="h-12 px-4 bg-gray-100 text-stone rounded-full font-medium hover:bg-gray-200 transition-all"
            >
              Clear
            </button>
          )}
        </div>
        {searchResults !== null && (
          <p className="text-stone text-sm mt-2">Found {searchResults.length} song{searchResults.length !== 1 ? 's' : ''} matching "{searchQuery}"</p>
        )}
      </div>

      {/* Mood Filter Tags */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-stone text-sm mr-2">Filter by mood:</span>
          {selectedMoodFilter && (
            <button
              onClick={() => setSelectedMoodFilter(null)}
              className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-200 text-charcoal hover:bg-gray-300 transition-all"
            >
              ✕ Clear
            </button>
          )}
          {moodOptions.map((mood) => (
            <button
              key={mood}
              onClick={() => setSelectedMoodFilter(selectedMoodFilter === mood ? null : mood)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                selectedMoodFilter === mood
                  ? 'bg-sage text-white shadow-md'
                  : 'bg-white border border-gray-200 text-stone hover:border-sage hover:text-sage'
              }`}
            >
              {mood}
            </button>
          ))}
        </div>
        {selectedMoodFilter && (
          <p className="text-sage text-sm mt-2">
            Showing {filteredSongs.length} {selectedMoodFilter.toLowerCase()} song{filteredSongs.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Collections Filter */}
      {collections && collections.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-stone text-sm mr-2">Collections:</span>
            {selectedCollectionFilter && (
              <button
                onClick={() => setSelectedCollectionFilter(null)}
                className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-200 text-charcoal hover:bg-gray-300 transition-all"
              >
                ✕ Clear
              </button>
            )}
            {collections.map((collection) => {
              const songCount = songs.filter(s => s.collection_ids?.includes(collection.id)).length;
              return (
                <button
                  key={collection.id}
                  onClick={() => setSelectedCollectionFilter(selectedCollectionFilter === collection.id ? null : collection.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                    selectedCollectionFilter === collection.id
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-white border border-gray-200 text-stone hover:border-amber-500 hover:text-amber-600'
                  }`}
                >
                  {collection.emoji || '📁'} {collection.name} ({songCount})
                </button>
              );
            })}
          </div>
          {selectedCollectionFilter && (
            <p className="text-amber-600 text-sm mt-2">
              Showing {filteredSongs.length} song{filteredSongs.length !== 1 ? 's' : ''} in collection
            </p>
          )}
        </div>
      )}

      {showForm && isAdmin && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-charcoal">{editingSong ? "Edit Song 🎵" : "Add New Song 🎵"}</h2>
              <button onClick={resetForm} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-charcoal font-medium mb-2">Song Title *</label><input data-testid="song-title-input" type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Title" required /></div>
                <div><label className="block text-charcoal font-medium mb-2">Artist 🎤</label><input type="text" value={formData.artist} onChange={(e) => setFormData({ ...formData, artist: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Artist" /></div>
              </div>
              
              <div><label className="block text-charcoal font-medium mb-2">Song Type 🎶</label>
                <div className="grid grid-cols-3 gap-2">
                  {songTypeOptions.map((opt) => (<button key={opt.value} type="button" onClick={() => setFormData({ ...formData, song_type: opt.value })} className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${formData.song_type === opt.value ? "border-terracotta bg-terracotta/10" : "border-gray-200"}`}><span className="text-2xl">{opt.emoji}</span><span className="text-xs mt-1">{opt.label}</span></button>))}
                </div>
              </div>

              <div><label className="block text-charcoal font-medium mb-2">Mood Tags 🎭 <span className="text-stone text-sm font-normal">(click to select)</span></label>
                <div className="flex flex-wrap gap-2">
                  {moodOptions.map((mood) => (
                    <button
                      key={mood}
                      type="button"
                      onClick={() => toggleMood(mood)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        (formData.moods || []).includes(mood)
                          ? "bg-sage text-white"
                          : "bg-gray-100 text-stone hover:bg-gray-200"
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </div>

              <div><label className="block text-charcoal font-medium mb-2">Audio File 🔊</label>
                {formData.audio_filename ? (
                  <div className="flex items-center gap-3 bg-sage/10 rounded-xl p-3">
                    <Music size={24} className="text-sage" /><span className="flex-1 truncate">{formData.audio_filename}</span>
                    <button type="button" onClick={() => setFormData({ ...formData, audio_file: "", audio_filename: "" })} className="text-red-500"><X size={20} /></button>
                  </div>
                ) : (
                  <label className="cursor-pointer block bg-sage/10 hover:bg-sage/20 border-2 border-dashed border-sage/30 rounded-xl p-4 text-center transition-all">
                    <input type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" /><Music size={32} className="mx-auto text-sage mb-2" /><span className="text-sage font-medium">Choose Audio (MP3, WAV up to 15MB)</span>
                  </label>
                )}
              </div>

              <div><label className="block text-charcoal font-medium mb-2">Cover Image 🖼️</label>
                <div className="flex items-center gap-4">
                  {formData.cover_image ? <div className="relative"><img src={formData.cover_image} alt="Cover" className="w-20 h-20 rounded-xl object-cover" /><button type="button" onClick={() => setFormData({ ...formData, cover_image: "" })} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={14} /></button></div> : <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center text-3xl">🎵</div>}
                  <label className="cursor-pointer bg-paper text-sage border-2 border-sage/20 hover:border-sage px-4 py-2 rounded-full transition-all"><input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "cover_image")} className="hidden" />Choose Cover</label>
                </div>
              </div>

              <div><label className="block text-charcoal font-medium mb-2">Illustrations 🎨 (Disney/Joni Mitchell style)</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.illustrations.map((img, idx) => (
                    <div key={idx} className="relative"><img src={img} alt="" className="w-20 h-20 rounded-xl object-cover" /><button type="button" onClick={() => removeIllustration(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={14} /></button></div>
                  ))}
                  <label className="cursor-pointer w-20 h-20 bg-gray-100 hover:bg-gray-200 rounded-xl flex flex-col items-center justify-center text-sage transition-all">
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "illustrations")} className="hidden" /><Plus size={24} /><span className="text-xs">Add</span>
                  </label>
                </div>
                <p className="text-sm text-stone">Add your custom illustrations to surround the song like a storybook ✨</p>
              </div>

              <div><label className="block text-charcoal font-medium mb-2">Lyrics 📜</label><textarea value={formData.lyrics} onChange={(e) => setFormData({ ...formData, lyrics: e.target.value })} className="w-full h-32 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none font-handwritten text-lg" placeholder="Enter the song lyrics..." /></div>

              <div><label className="block text-charcoal font-medium mb-2">Story 📖</label><textarea value={formData.story_text} onChange={(e) => setFormData({ ...formData, story_text: e.target.value })} className="w-full h-24 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none" placeholder="Add a story that goes with this song..." /></div>

              <div><label className="block text-charcoal font-medium mb-2">Notes 📝</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full h-20 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none" placeholder="Any additional notes..." /></div>

              <button data-testid="submit-song-button" type="submit" disabled={loading} className="w-full bg-terracotta text-white py-4 rounded-full text-lg font-medium hover:bg-terracotta-dark transition-all disabled:opacity-50">{loading ? "Saving..." : editingSong ? "Update Song 🎵" : "Add Song 🎵"}</button>
              <button type="button" onClick={resetForm} className="w-full bg-gray-100 text-charcoal py-3 rounded-full font-medium hover:bg-gray-200 transition-all mt-2">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {filteredByLetter.length === 0 && initialSongId ? (
        <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50"><span className="text-5xl block mb-4 animate-pulse">🎵</span><h3 className="font-heading text-xl text-charcoal mb-2">Loading song...</h3><p className="text-stone mb-6">Please wait while we fetch your song</p></div>
      ) : filteredByLetter.length === 0 ? (
        <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50"><span className="text-5xl block mb-4">🎵</span><h3 className="font-heading text-xl text-charcoal mb-2">{selectedLetter ? `No songs starting with "${selectedLetter}"` : "No songs yet"}</h3><p className="text-stone mb-6">{selectedLetter ? "Try another letter" : "Start building your songbook!"}</p>{!selectedLetter && <button onClick={() => setShowForm(true)} className="bg-terracotta text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-terracotta-dark transition-all">Add Your First Song 🎶</button>}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredByLetter.length === 0 && showFavoritesOnly ? (
            <div className="col-span-full text-center py-12">
              <div className="text-5xl mb-4">⭐</div>
              <p className="text-stone">No favorite songs yet. Mark songs as favorites to see them here!</p>
            </div>
          ) : filteredByLetter.map((song) => (
            <div key={song.id} data-testid={`song-card-${song.id}`} className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-white/50 hover:-translate-y-1 relative">
              {/* Favorite Star */}
              {isAdmin && (
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(song.id, song.is_favorite); }}
                  className="absolute top-3 left-3 z-10 p-2 bg-white/90 rounded-full shadow-sm hover:scale-110 transition-all"
                >
                  <Star size={20} className={song.is_favorite ? 'fill-amber-400 text-amber-400' : 'text-gray-400'} />
                </button>
              )}
              {!isAdmin && song.is_favorite && (
                <div className="absolute top-3 left-3 z-10 p-2 bg-amber-100 rounded-full">
                  <Star size={18} className="fill-amber-400 text-amber-400" />
                </div>
              )}
              <div onClick={() => handleViewSong(song)} className="cursor-pointer">
                <div className="h-40 bg-gradient-to-br from-terracotta/20 to-sandy/20 relative">
                  {song.cover_image ? <img src={song.cover_image} alt={song.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-5xl">🎵</div>}
                  {song.song_type && <span className="absolute top-3 right-3 bg-white/90 px-3 py-1 rounded-full text-sm">{songTypeOptions.find(o => o.value === song.song_type)?.emoji}</span>}
                  {(song.audio_file || song.audio_file_id) && <span className="absolute bottom-3 left-3 bg-sage text-white px-3 py-1 rounded-full text-sm flex items-center gap-1"><Play size={14} />Audio</span>}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-charcoal mb-1">{song.title}</h3>
                  {song.artist && <p className="text-stone mb-2">🎤 {song.artist}</p>}
                  {song.song_type && <p className="text-stone text-sm">{songTypeOptions.find(o => o.value === song.song_type)?.label}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== BIRTHDAY REMINDER BANNER ====================
const BirthdayBanner = ({ pets, onDismiss, onViewPet }) => {
  const [dismissed, setDismissed] = useState(() => {
    const saved = localStorage.getItem('pawsBirthdayDismissed');
    if (saved) {
      try {
        const { date, petIds } = JSON.parse(saved);
        // Reset dismissals if it's a new day
        if (date !== new Date().toDateString()) return [];
        return petIds;
      } catch (e) { return []; }
    }
    return [];
  });

  const getBirthdayPets = () => {
    const today = new Date();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();
    
    return pets.filter(pet => {
      if (!pet.birthday || dismissed.includes(pet.id)) return false;
      const bday = new Date(pet.birthday);
      const bdayMonth = bday.getMonth();
      const bdayDay = bday.getDate();
      
      // Check if birthday is today or within next 3 days
      for (let i = 0; i <= 3; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(today.getDate() + i);
        if (checkDate.getMonth() === bdayMonth && checkDate.getDate() === bdayDay) {
          return true;
        }
      }
      return false;
    }).map(pet => {
      const bday = new Date(pet.birthday);
      const today = new Date();
      const bdayThisYear = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
      const diffDays = Math.ceil((bdayThisYear - today) / (1000 * 60 * 60 * 24));
      return { ...pet, daysUntil: diffDays < 0 ? diffDays + 365 : diffDays };
    });
  };

  const birthdayPets = getBirthdayPets();

  const handleDismiss = (petId) => {
    const newDismissed = [...dismissed, petId];
    setDismissed(newDismissed);
    localStorage.setItem('pawsBirthdayDismissed', JSON.stringify({
      date: new Date().toDateString(),
      petIds: newDismissed
    }));
  };

  if (birthdayPets.length === 0) return null;

  return (
    <div className="relative z-40 max-w-2xl mx-auto px-4 mb-4">
      {birthdayPets.map(pet => (
        <div key={pet.id} className="bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 rounded-2xl p-1 mb-3 shadow-lg">
          <div className="bg-white rounded-xl p-4 flex items-center gap-4">
            <div className="text-4xl">🎂</div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-charcoal">
                {pet.daysUntil === 0 ? (
                  <>Happy Birthday, {pet.name}! 🎉🎈</>
                ) : pet.daysUntil === 1 ? (
                  <>{pet.name}'s birthday is tomorrow! 🎁</>
                ) : (
                  <>{pet.name}'s birthday is in {pet.daysUntil} days! 🎁</>
                )}
              </h3>
              <p className="text-stone text-sm">
                {pet.daysUntil === 0 
                  ? "Time to celebrate your furry friend! 🥳" 
                  : "Don't forget to plan something special!"}
              </p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => onViewPet(pet)} 
                className="px-4 py-2 bg-terracotta text-white rounded-full text-sm font-medium hover:bg-terracotta-dark transition-all"
              >
                View
              </button>
              <button 
                onClick={() => handleDismiss(pet.id)} 
                className="p-2 text-stone hover:text-charcoal transition-all"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== WELCOME MODAL ====================
const WelcomeModal = ({ onClose, userName }) => {
  const [currentPage, setCurrentPage] = useState(0);
  
  // Close and save to localStorage
  const handleClose = () => {
    try {
      localStorage.setItem('pawsHasSeenWelcome', 'true');
    } catch (e) {
      console.error("Could not save to localStorage:", e);
    }
    onClose();
  };
  
  const pages = [
    // Welcome
    {
      content: (
        <div className="text-center">
          <div className="text-6xl mb-6">🕯️</div>
          <h2 className="font-heading text-3xl font-bold text-charcoal mb-4">Welcome to Paws & Harmony</h2>
          <p className="font-handwritten text-2xl text-sage mb-6">Music • Reflections • Comfort for Pets & People</p>
          <p className="text-stone leading-relaxed max-w-md mx-auto">
            This is a gentle space for music, love, and the animals who fill our lives with meaning.
          </p>
          <p className="text-stone leading-relaxed max-w-md mx-auto mt-4">
            Created for quiet moments — when the world feels heavy, or when a beloved pet is near.
          </p>
          <p className="font-handwritten text-xl text-amber-700 mt-6">
            Take a slow breath. Let the lantern glow.
          </p>
        </div>
      )
    },
    // Dedication
    {
      content: (
        <div className="text-center">
          <div className="text-5xl mb-6">💕</div>
          <h2 className="font-heading text-2xl font-semibold text-charcoal mb-6">Dedication</h2>
          <div className="max-w-lg mx-auto text-stone leading-relaxed space-y-4">
            <p>
              For the ones who greet us at the door,<br/>
              who curl beside us when the world is loud,<br/>
              and who love us before we've had our coffee.
            </p>
            <p>
              For dogs with wagging tails and knowing eyes.<br/>
              For cats who choose our laps as thrones.<br/>
              For horses who carry our hearts on quiet trails.
            </p>
            <p>
              For every bird, bunny, fish, and ferret—<br/>
              and every rescue who waited just for us.
            </p>
            <p className="font-medium text-charcoal">
              You are our teachers, our healers, our joy, our safe place.
            </p>
            <p className="text-amber-700 italic mt-4">
              You are the lanterns that light our way home.
            </p>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-stone text-sm">🌈 In memory of those whose light still shines in our hearts 🌈</p>
          </div>
        </div>
      )
    },
    // What's Inside
    {
      content: (
        <div className="text-center">
          <div className="text-5xl mb-6">✨</div>
          <h2 className="font-heading text-2xl font-semibold text-charcoal mb-6">What You'll Find Here</h2>
          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
            <div className="bg-sage/10 rounded-2xl p-4">
              <span className="text-2xl">🎵</span>
              <p className="text-charcoal font-medium mt-2">Songbook</p>
              <p className="text-stone text-sm">Music for pets & people</p>
            </div>
            <div className="bg-terracotta/10 rounded-2xl p-4">
              <span className="text-2xl">📔</span>
              <p className="text-charcoal font-medium mt-2">Journal</p>
              <p className="text-stone text-sm">Your thoughts & gratitude</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-4">
              <span className="text-2xl">🐾</span>
              <p className="text-charcoal font-medium mt-2">Pet Profiles</p>
              <p className="text-stone text-sm">Celebrate your companions</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-4">
              <span className="text-2xl">🕯️</span>
              <p className="text-charcoal font-medium mt-2">Comfort Corner</p>
              <p className="text-stone text-sm">Peace & reflection</p>
            </div>
          </div>
          <p className="font-handwritten text-xl text-sage mt-6">
            Every note is a lantern — guiding us home to peace.
          </p>
        </div>
      )
    }
  ];

  return (
    <div 
      className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-3xl p-8 max-w-xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-amber-200 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-stone hover:text-charcoal p-2 transition-all"
          title="Close"
        >
          <X size={24} />
        </button>
        
        {pages[currentPage].content}
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-amber-200">
          <button 
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            className={`px-4 py-2 rounded-full font-medium transition-all ${currentPage === 0 ? 'text-stone hover:text-charcoal cursor-pointer' : 'text-stone hover:text-charcoal'}`}
          >
            {currentPage === 0 ? (
              <span onClick={handleClose} className="text-stone hover:text-sage">Skip intro →</span>
            ) : '← Back'}
          </button>
          
          {/* Dots */}
          <div className="flex gap-2">
            {pages.map((_, idx) => (
              <div 
                key={idx} 
                className={`w-2 h-2 rounded-full transition-all ${idx === currentPage ? 'bg-amber-500 w-6' : 'bg-amber-200'}`}
              />
            ))}
          </div>
          
          {currentPage < pages.length - 1 ? (
            <button 
              onClick={() => setCurrentPage(currentPage + 1)}
              className="px-6 py-2 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-all"
            >
              Next →
            </button>
          ) : (
            <button 
              onClick={handleClose}
              className="px-6 py-2 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all"
            >
              Enter 🐾
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== AUTH PAGE ====================
const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password || (!isLogin && !name)) {
      setError("Please fill in all fields");
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email, password } : { email, password, name };
      console.log("Attempting login to:", `${API}${endpoint}`);
      const response = await axios.post(`${API}${endpoint}`, payload, { timeout: 15000 });
      console.log("Login response received:", response.status);
      
      if (response.data?.access_token) {
        localStorage.setItem("pawsToken", response.data.access_token);
        localStorage.setItem("pawsUser", JSON.stringify(response.data.user));
        console.log("Token saved, calling onLogin");
        toast.success(isLogin ? "Welcome back! 🐾" : "Welcome to Paws & Harmony! 🎉");
        onLogin(response.data.user);
      } else {
        console.error("No access_token in response:", response.data);
        setError("Login failed - invalid response");
        toast.error("Login failed - please try again");
      }
    } catch (error) {
      console.error("Login error:", error);
      const msg = error.code === 'ECONNABORTED' 
        ? "Connection timed out. Please try again."
        : error.response?.data?.detail || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <img src={WELCOME_BACKGROUNDS[0]} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-sage/20 to-terracotta/20" />
      </div>
      
      <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-8 w-full max-w-md shadow-xl border border-white/50">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🐾</div>
          <h1 className="font-heading text-3xl font-bold text-charcoal">Paws & Harmony</h1>
          <p className="font-handwritten text-xl text-sage mt-2">Your peaceful pet journal 💕</p>
        </div>

        <div className="flex bg-gray-100 rounded-full p-1 mb-6">
          <button onClick={() => setIsLogin(true)} className={`flex-1 py-2 px-4 rounded-full font-medium transition-all ${isLogin ? 'bg-white shadow text-charcoal' : 'text-stone'}`}>Sign In</button>
          <button onClick={() => setIsLogin(false)} className={`flex-1 py-2 px-4 rounded-full font-medium transition-all ${!isLogin ? 'bg-white shadow text-charcoal' : 'text-stone'}`}>Sign Up</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone" size={20} />
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone" size={20} />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone" size={20} />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-12 pr-12 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal transition-all"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? "Please wait..." : (isLogin ? "Sign In" : "Create Account")}
          </button>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-center">
              {error}
            </div>
          )}
        </form>

        <p className="text-center text-stone text-sm mt-6">
          {isLogin ? "New here? " : "Already have an account? "}
          <button onClick={() => { setIsLogin(!isLogin); setError(""); }} className="text-sage font-medium hover:underline">
            {isLogin ? "Create an account" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [initialSongId, setInitialSongId] = useState(null); // For Song of the Day direct link
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showAuthForce, setShowAuthForce] = useState(false); // Force show auth page for Sign In button
  
  // Check for shared song URL parameter on load (e.g., ?song=abc123)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedSongId = urlParams.get('song');
    if (sharedSongId) {
      setInitialSongId(sharedSongId);
      setCurrentPage("songbook");
      // Don't clear URL yet - let the songbook page handle that after opening the song
    }
  }, []);
  const [stats, setStats] = useState({ pets: 0, songs: 0, moments: 0, categories: 0, mood_entries: 0 });
  const [pets, setPets] = useState([]);
  const [songs, setSongs] = useState([]);
  const [moments, setMoments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [moodEntries, setMoodEntries] = useState([]);
  const [gratitudeEntries, setGratitudeEntries] = useState([]);
  const [stories, setStories] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [shareTokens, setShareTokens] = useState([]);
  const [happyEndings, setHappyEndings] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMood, setCurrentMood] = useState(null);

  // Check for existing auth on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('pawsUser');
    const token = localStorage.getItem('pawsToken');
    if (savedUser && token) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('pawsUser');
        localStorage.removeItem('pawsToken');
      }
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => { const saved = localStorage.getItem('pawsHarmonyMood'); if (saved) { try { setCurrentMood(JSON.parse(saved)); } catch (e) {} } }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    // Show welcome for new users (check if they've seen it before)
    const hasSeenWelcome = localStorage.getItem('pawsHasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  };

  const handleCloseWelcome = () => {
    localStorage.setItem('pawsHasSeenWelcome', 'true');
    setShowWelcome(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('pawsToken');
    localStorage.removeItem('pawsUser');
    setUser(null);
    toast.success("Logged out 👋");
  };

  const fetchStats = async () => { try { const r = await axiosAuth.get(`${API}/stats`); setStats(r.data); } catch (e) {} };
  const fetchPets = async () => { try { const r = await axiosAuth.get(`${API}/pets`); setPets(r.data); } catch (e) {} };
  
  // Songs fetch with retry logic for reliability
  const fetchSongs = async (retries = 3) => { 
    for (let attempt = 1; attempt <= retries; attempt++) {
      try { 
        const r = await axios.get(`${API}/songs`, { timeout: 15000 }); 
        if (r.data && r.data.length > 0) {
          setSongs(r.data);
          return;
        }
      } catch (e) { 
        console.error(`Failed to fetch songs (attempt ${attempt}):`, e); 
      }
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
      }
    }
  };
  
  const fetchMoments = async () => { try { const r = await axiosAuth.get(`${API}/moments`); setMoments(r.data); } catch (e) { console.error("Failed to fetch moments:", e); } };
  const fetchCategories = async () => { try { const r = await axiosAuth.get(`${API}/categories`); setCategories(r.data); } catch (e) { console.error("Failed to fetch categories:", e); } };
  const fetchCollections = async () => { try { const r = await axios.get(`${API}/collections`); setCollections(r.data); } catch (e) { console.error("Failed to fetch collections:", e); } };
  const fetchMoodEntries = async () => { try { const r = await axiosAuth.get(`${API}/mood-entries`); setMoodEntries(r.data); } catch (e) { console.error("Failed to fetch mood entries:", e); } };
  const fetchGratitude = async () => { try { const r = await axiosAuth.get(`${API}/gratitude`); setGratitudeEntries(r.data); } catch (e) { console.error("Failed to fetch gratitude:", e); } };
  const fetchStories = async () => { try { const r = await axiosAuth.get(`${API}/stories`); setStories(r.data); } catch (e) { console.error("Failed to fetch stories:", e); } };
  const fetchMilestones = async () => { try { const r = await axiosAuth.get(`${API}/milestones`); setMilestones(r.data); } catch (e) { console.error("Failed to fetch milestones:", e); } };
  const fetchShareTokens = async () => { try { const r = await axiosAuth.get(`${API}/share`); setShareTokens(r.data); } catch (e) { console.error("Failed to fetch share tokens:", e); } };
  const fetchHappyEndings = async () => { try { const r = await axios.get(`${API}/happy-endings`); setHappyEndings(r.data); } catch (e) { console.error("Failed to fetch happy endings:", e); } };

  const saveMoodEntry = async (mood, note) => { try { await axiosAuth.post(`${API}/mood-entries`, { mood_emoji: mood.emoji, mood_name: mood.name, note: note || "" }); await fetchMoodEntries(); await fetchStats(); } catch (e) {} };

  // Refresh all data function - can be called manually if data fails to load
  const refreshAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(), fetchPets(), fetchSongs(), fetchMoments(), 
        fetchCategories(), fetchMoodEntries(), fetchGratitude(), 
        fetchStories(), fetchMilestones(), fetchShareTokens(), fetchHappyEndings()
      ]);
      toast.success("Data refreshed!");
    } catch (e) {
      console.error("Error refreshing data:", e);
      toast.error("Some data failed to load. Please try again.");
    }
    setLoading(false);
  };

  // Fetch public data (songs, happy endings) immediately - no login required
  useEffect(() => {
    fetchSongs();
    fetchHappyEndings();
    fetchCollections();
  }, []);

  // Fetch private data when user logs in
  useEffect(() => { 
    if (user) {
      (async () => { setLoading(true); await Promise.all([fetchStats(), fetchPets(), fetchMoments(), fetchCategories(), fetchMoodEntries(), fetchGratitude(), fetchStories(), fetchMilestones(), fetchShareTokens()]); setLoading(false); })(); 
    }
  }, [user]);

  const renderPage = () => {
    switch (currentPage) {
      case "home": return <HomePage stats={stats} setCurrentPage={setCurrentPage} currentMood={currentMood} user={user} songs={songs} setInitialSongId={setInitialSongId} onShowAuth={() => setShowAuthForce(true)} />;
      case "pets": return <PetsPage pets={pets} fetchPets={fetchPets} />;
      case "songbook": return <SongbookPage songs={songs} fetchSongs={fetchSongs} categories={categories} pets={pets} isAdmin={user?.is_admin} initialSongId={initialSongId} clearInitialSong={() => setInitialSongId(null)} collections={collections} />;
      case "storybook": return <StorybookCreatorPage pets={pets} songs={songs} />;
      case "stories": return <HappyEndingsPage happyEndings={happyEndings} fetchHappyEndings={fetchHappyEndings} isAdmin={user?.is_admin} />;
      case "moments": return <MomentsPage moments={moments} fetchMoments={fetchMoments} pets={pets} categories={categories} />;
      case "journal": return <JournalPage moodEntries={moodEntries} fetchMoodEntries={fetchMoodEntries} gratitudeEntries={gratitudeEntries} fetchGratitude={fetchGratitude} stories={stories} fetchStories={fetchStories} milestones={milestones} fetchMilestones={fetchMilestones} pets={pets} songs={songs} />;
      case "comfort": return <ComfortCornerPage />;
      case "share": return <SharePage shareTokens={shareTokens} fetchShareTokens={fetchShareTokens} user={user} />;
      case "guide": return <HowToUsePage setCurrentPage={setCurrentPage} />;
      case "song-manager": return <SongManagerPage />;
      case "collections": return <CollectionsPage songs={songs} fetchSongs={fetchSongs} isAdmin={user?.is_admin} />;
      default: return <HomePage stats={stats} setCurrentPage={setCurrentPage} currentMood={currentMood} user={user} songs={songs} setInitialSongId={setInitialSongId} onShowAuth={() => setShowAuthForce(true)} />;
    }
  };

  // Show auth check loading
  if (!authChecked) return <div className="min-h-screen bg-paper flex items-center justify-center"><div className="text-6xl animate-bounce">🐾</div></div>;
  
  // Public pages - viewable without login (Songbook, Home, Happy Endings)
  const publicPages = ["home", "songbook", "stories"];
  const isPublicPage = publicPages.includes(currentPage);
  
  // Show auth if forced (Sign In button clicked) or if trying to access private pages
  if (showAuthForce || (!user && !isPublicPage)) {
    return <AuthPage onLogin={(userData) => { handleLogin(userData); setShowAuthForce(false); }} />;
  }

  if (loading && user) return <div className="min-h-screen bg-paper flex items-center justify-center"><div className="text-center"><div className="text-6xl mb-4 animate-bounce">🐾</div><p className="text-stone text-lg">Loading your peaceful space...</p></div></div>;

  const handleViewBirthdayPet = (pet) => {
    setCurrentPage("pets");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('viewPet', { detail: pet }));
    }, 100);
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <WelcomeBackground mood={currentMood} />
      <Toaster position="top-center" richColors />
      {showWelcome && <WelcomeModal onClose={handleCloseWelcome} userName={user?.name} />}
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} currentMood={currentMood} setCurrentMood={setCurrentMood} saveMoodEntry={saveMoodEntry} user={user} onLogout={handleLogout} onRefresh={refreshAllData} onShowAuth={() => setShowAuthForce(true)} onShowWelcome={() => setShowWelcome(true)} songs={songs} pets={pets} />
      <main className="pt-24 md:pt-28 pb-24 md:pb-8 px-4 md:px-8 relative">
        <div className="max-w-6xl mx-auto">
          <BirthdayBanner pets={pets} onViewPet={handleViewBirthdayPet} />
          {renderPage()}
        </div>
      </main>
    </div>
  );
}

export default App;
